//src/pages/record/NewPrescription.jsx
import React, { useRef, useEffect, useState } from "react";
import {
  TextField,
  Button,
  Grid,
  Checkbox,
  FormControlLabel,
  Autocomplete,
  Typography,
  InputAdornment,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  MenuItem,
  IconButton,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  CircularProgress,
  Divider,
  Alert,
} from "@mui/material";
import { Formik, Form, FieldArray } from "formik";
import * as Yup from "yup";
import { getComplainList } from "../../services/complaintService";
import { getDiagnosisList } from "../../services/diagnosisService";
import { getAllLabTests } from "../../services/labtestService";
import { getAllMedicine, createNewMedicine } from "../../services/medicineNewService";
import AddNewMedicine from "../inventory/Product/AddNewMedicine";
import CreateNewLabTest from "../inventory/PurchaseInvoice/CreateNewLabTest";
import { createLabTestInBaseTable } from "../../services/labtestService";
import {
  createPrescription,
  getPrescriptionsByPatientId,
  getPrescriptionById,
} from "../../services/prescriptionService";
import { getByPrescriptionId as getComplaintsByPrescriptionId } from "../../services/PrescriptionComplaintService";
import { getPatientById } from "../../services/patientService";
import apiClient from "../../services/apiClient";
import DeleteIcon from "@mui/icons-material/Delete";
import CloseIcon from "@mui/icons-material/Close";
import UndoOutlinedIcon from "@mui/icons-material/UndoOutlined";
import {
  getFrontDeskVitalSignsForVisit,
  normalizeVitalSignsRecord,
} from "../../services/vitalSignsService";
import { confirmAsync } from "../../utils/appNotify";
import { useSelector } from 'react-redux';
import { toast } from "react-toastify";
import dayjs from "dayjs";
import {
  fetchOrganizationProfile,
  getOrganizationId,
  getOrganizationName,
} from "../../utils/organizationUtils";
import {
  buildOpdSummaryDocumentHtml,
  printPatientSummaryDocument,
} from "../../utils/patientSummaryDocumentUtils";
import { getAllDoctors } from "../../services/doctorService";
import {
  DENSE_FIELD_PROPS,
  DENSE_FORM_BODY_SX,
  FORM_CANCEL_BUTTON_SX,
  FORM_MODAL_CONTENT_SX,
  FORM_MODAL_FOOTER_ACTIONS_SX,
  FORM_MODAL_FOOTER_SX,
  FORM_SECTION_TITLE_SX,
  FORM_SUBMIT_BUTTON_SX,
  STANDARD_FORM_DIALOG_MARGIN,
  STANDARD_FORM_MODAL_MAX_HEIGHT,
} from "../../utils/formModalStyles";

const dosageOptions = [
  "1-0-0",
  "1-0-1",
  "1-1-0",
  "1-1-1",
  "0-0-1",
  "0-1-0",
  "0-1-1",
  "0-0-0",
];
const frequencyOptions = ["daily", "weekly", "monthly"];
const HALF_FIELD_GRID_SX = { xs: 12, md: 6 };
const VITAL_SIGN_GRID_SX = { xs: 6, sm: 4, md: 2 };

const resolveAppointmentId = (raw) => {
  if (raw == null || raw === "") return null;
  if (typeof raw === "object") {
    const nested =
      raw.appointmentId ??
      raw.appointment_id ??
      raw.id ??
      raw?.appointment?.id ??
      raw?.appointment?.appointmentId ??
      raw?.data?.id ??
      raw?.data?.appointmentId ??
      null;
    return resolveAppointmentId(nested);
  }
  const asString = String(raw).trim();
  if (
    !asString ||
    asString.startsWith("patient-only-") ||
    asString === "—" ||
    asString.toLowerCase() === "null" ||
    asString === "0"
  ) {
    return null;
  }
  const asNumber = Number(asString);
  if (Number.isFinite(asNumber) && asNumber > 0) return asNumber;
  // Preserve non-numeric IDs (e.g. UUIDs) for display/print
  return asString;
};

/** Try each candidate independently so empty strings don't block later sources. */
const resolveAppointmentIdFrom = (...candidates) => {
  for (const raw of candidates) {
    const id = resolveAppointmentId(raw);
    if (id != null) return id;
  }
  return null;
};

const resolvePositiveId = (...candidates) => {
  for (const raw of candidates) {
    if (raw == null || raw === "") continue;
    if (typeof raw === "object") {
      const nested = resolvePositiveId(
        raw.id,
        raw.patientId,
        raw.doctorId,
        raw.appointmentId
      );
      if (nested != null) return nested;
      continue;
    }
    const n = Number(raw);
    if (Number.isFinite(n) && n > 0) return n;
  }
  return null;
};

/** Strip units / junk so prefilled front-desk vitals don't silently fail Yup. */
const sanitizeVitalNumericValue = (value) => {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const match = raw.match(/^\d+(\.\d+)?/);
  return match ? match[0] : "";
};

const sanitizeBpReadingValue = (value) => {
  if (value == null) return "";
  const raw = String(value).trim();
  if (!raw) return "";
  const match = raw.match(/^\d+(\.\d+)?(\/\d+(\.\d+)?)?/);
  return match ? match[0] : "";
};

const sanitizeVitalSignsForSubmit = (vitalSigns = {}) => ({
  bpReading: sanitizeBpReadingValue(vitalSigns.bpReading),
  weightReading: sanitizeVitalNumericValue(vitalSigns.weightReading),
  heightReading: sanitizeVitalNumericValue(vitalSigns.heightReading),
  oxygenSaturation: sanitizeVitalNumericValue(vitalSigns.oxygenSaturation),
  pulseRate: sanitizeVitalNumericValue(vitalSigns.pulseRate),
  respirationRate: sanitizeVitalNumericValue(vitalSigns.respirationRate),
});

/** Empty strings (`""`) make Jackson 400 when the backend field is numeric. */
const buildVitalSignsPayload = (sanitizedVitals = {}) => {
  const payload = {};
  if (sanitizedVitals.bpReading) {
    payload.bpReading = sanitizedVitals.bpReading;
  }
  ["weightReading", "heightReading", "oxygenSaturation", "pulseRate", "respirationRate"].forEach(
    (key) => {
      const raw = sanitizedVitals[key];
      if (raw === "" || raw == null) return;
      const n = Number(raw);
      payload[key] = Number.isFinite(n) ? n : raw;
    }
  );
  return Object.keys(payload).length > 0 ? [payload] : [];
};

const getApiErrorMessage = (error, fallback) => {
  const data = error?.response?.data;
  if (typeof data === "string" && data.trim()) return data.trim();
  const nested =
    data?.message ||
    data?.error ||
    data?.detail ||
    data?.title ||
    (Array.isArray(data?.errors) ? data.errors[0]?.defaultMessage || data.errors[0]?.message : null);
  if (typeof nested === "string" && nested.trim()) return nested.trim();
  if (error?.response?.status === 400) {
    return "The server rejected this prescription (400). A field was invalid or incomplete — refill and submit again, or check Network → Response for the exact reason.";
  }
  return error?.message || fallback;
};

const collectValidationMessages = (errors, prefix = "") => {
  if (!errors) return [];
  if (typeof errors === "string") {
    return [prefix ? `${prefix}: ${errors}` : errors];
  }
  if (Array.isArray(errors)) {
    return errors.flatMap((item, index) =>
      collectValidationMessages(item, prefix ? `${prefix}[${index}]` : `[${index}]`)
    );
  }
  if (typeof errors === "object") {
    return Object.entries(errors).flatMap(([key, value]) =>
      collectValidationMessages(value, prefix ? `${prefix}.${key}` : key)
    );
  }
  return [];
};

const touchValidationErrors = (errors, setFieldTouched, prefix = "") => {
  if (!errors || typeof setFieldTouched !== "function") return;
  if (typeof errors === "string") {
    if (prefix) setFieldTouched(prefix, true, false);
    return;
  }
  if (Array.isArray(errors)) {
    errors.forEach((item, index) => {
      touchValidationErrors(
        item,
        setFieldTouched,
        prefix ? `${prefix}.${index}` : String(index)
      );
    });
    return;
  }
  if (typeof errors === "object") {
    Object.entries(errors).forEach(([key, value]) => {
      touchValidationErrors(
        value,
        setFieldTouched,
        prefix ? `${prefix}.${key}` : key
      );
    });
  }
};

const getLatestPrescription = (prescriptions) => {
  if (!Array.isArray(prescriptions) || prescriptions.length === 0) {
    return null;
  }
  const sorted = [...prescriptions].sort((a, b) => {
    const idDiff =
      (b.id ?? b.prescriptionId ?? 0) - (a.id ?? a.prescriptionId ?? 0);
    if (idDiff !== 0) return idDiff;
    return (
      new Date(b.createdAt || b.prescriptionDate || 0) -
      new Date(a.createdAt || a.prescriptionDate || 0)
    );
  });
  return sorted[0];
};

const getLatestVitalSign = (vitalSigns) => {
  if (!vitalSigns) return null;
  if (!Array.isArray(vitalSigns)) {
    return typeof vitalSigns === "object" ? vitalSigns : null;
  }
  if (vitalSigns.length === 0) return null;
  if (vitalSigns.length === 1) return vitalSigns[0];

  const sorted = [...vitalSigns].sort((a, b) => {
    const aId = a.id || a.vitalSignId;
    const bId = b.id || b.vitalSignId;
    if (aId != null && bId != null) {
      return Number(bId) - Number(aId);
    }
    if (a.recordedAt && b.recordedAt) {
      return new Date(b.recordedAt) - new Date(a.recordedAt);
    }
    return 0;
  });
  return sorted[0];
};

/** Prefills empty vitals once from front-desk / appointment-linked readings. */
const PrefillFrontDeskVitals = ({ frontDeskVitals, values, setFieldValue }) => {
  const appliedRef = useRef(false);

  useEffect(() => {
    if (!frontDeskVitals || appliedRef.current) return;

    const current = values?.vitalSigns || {};
    const hasAnyCurrent = Object.values(current).some(
      (v) => v !== null && v !== undefined && String(v).trim() !== ""
    );
    if (hasAnyCurrent) {
      appliedRef.current = true;
      return;
    }

    const normalized = normalizeVitalSignsRecord(frontDeskVitals) || frontDeskVitals;
    setFieldValue(
      "vitalSigns.bpReading",
      sanitizeBpReadingValue(normalized.bpReading || "")
    );
    setFieldValue(
      "vitalSigns.weightReading",
      sanitizeVitalNumericValue(normalized.weightReading || "")
    );
    setFieldValue(
      "vitalSigns.heightReading",
      sanitizeVitalNumericValue(normalized.heightReading || "")
    );
    setFieldValue(
      "vitalSigns.pulseRate",
      sanitizeVitalNumericValue(normalized.pulseRate || "")
    );
    setFieldValue(
      "vitalSigns.respirationRate",
      sanitizeVitalNumericValue(normalized.respirationRate || "")
    );
    setFieldValue(
      "vitalSigns.oxygenSaturation",
      sanitizeVitalNumericValue(normalized.oxygenSaturation || "")
    );
    appliedRef.current = true;
  }, [frontDeskVitals, values?.vitalSigns, setFieldValue]);

  return null;
};

// Define Yup validation schema
const validationSchema = Yup.object({
  complaints: Yup.array().test(
    "at-least-one-complaint",
    "At least one complaint is required (either select from list or enter manually)",
    function(value) {
      const { additionalComplaints } = this.parent;
      const hasSelectedComplaints = value && value.length > 0;
      const hasManualComplaints = additionalComplaints && additionalComplaints.trim().length > 0;
      return hasSelectedComplaints || hasManualComplaints;
    }
  ),
  diagnoses: Yup.array().test(
    "at-least-one-diagnosis",
    "At least one diagnosis is required (either select from list or enter manually)",
    function(value) {
      const { additionalDiagnoses } = this.parent;
      const hasSelectedDiagnoses = value && value.length > 0;
      const hasManualDiagnoses = additionalDiagnoses && additionalDiagnoses.trim().length > 0;
      return hasSelectedDiagnoses || hasManualDiagnoses;
    }
  ),
  additionalComplaints: Yup.string().optional(),
  additionalDiagnoses: Yup.string().optional(),
  medicines: Yup.array()
    .of(
      Yup.object({
        name: Yup.string().nullable().optional(),
        dosage: Yup.string()
          .nullable()
          .when("name", {
            is: (name) => Boolean(name && String(name).trim()),
            then: (schema) => schema.required("Dosage is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),
        frequency: Yup.string()
          .nullable()
          .when("name", {
            is: (name) => Boolean(name && String(name).trim()),
            then: (schema) => schema.required("Frequency is required"),
            otherwise: (schema) => schema.optional().nullable(),
          }),
        duration: Yup.string()
          .nullable()
          .when("name", {
            is: (name) => Boolean(name && String(name).trim()),
            then: (schema) =>
              schema
                .required("Duration is required")
                .test("is-number", "Duration must be a number", function (value) {
                  if (!value || String(value).trim() === "") return false;
                  return !isNaN(Number(value)) && Number(value) > 0;
                }),
            otherwise: (schema) => schema.optional().nullable(),
          }),
        notes: Yup.string().optional("Medicine notes is optional"),
      })
    )
    .test(
      "at-least-one-medicine",
      "At least one medicine is required",
      function (medicines) {
        const list = Array.isArray(medicines) ? medicines : [];
        const hasOne = list.some((med) =>
          Boolean(med?.name && String(med.name).trim())
        );
        if (hasOne) return true;
        // Attach to first row medicine name so red helper text shows in the UI
        return this.createError({
          path: `${this.path}[0].name`,
          message: "At least one medicine is required",
        });
      }
    ),
  medicineNotes: Yup.string().optional("Medicine notes is optional"), // Common notes field for all medicines (UI only)
  vitalSigns: Yup.object({
    bpReading: Yup.string().test(
      "is-bp-or-empty",
      "BP Reading must be a number or systolic/diastolic (e.g. 120/80)",
      function (value) {
        if (!value || value.trim() === "") return true;
        return /^\d+(\.\d+)?(\/\d+(\.\d+)?)?$/.test(value.trim());
      }
    ),
    weightReading: Yup.string().test("is-number-or-empty", "Weight Reading must be a number", function(value) {
      if (!value || value.trim() === "") return true; // Allow empty
      return !isNaN(Number(value)) && Number(value) >= 0;
    }),
    heightReading: Yup.string().test("is-number-or-empty", "Height Reading must be a number", function(value) {
      if (!value || value.trim() === "") return true; // Allow empty
      return !isNaN(Number(value)) && Number(value) >= 0;
    }),
    oxygenSaturation: Yup.string().test("is-number-or-empty", "Oxygen Saturation must be a number", function(value) {
      if (!value || value.trim() === "") return true; // Allow empty
      return !isNaN(Number(value)) && Number(value) >= 0;
    }),
    pulseRate: Yup.string().test("is-number-or-empty", "Pulse Rate must be a number", function(value) {
      if (!value || value.trim() === "") return true; // Allow empty
      return !isNaN(Number(value)) && Number(value) >= 0;
    }),
    respirationRate: Yup.string().test("is-number-or-empty", "Respiration Rate must be a number", function(value) {
      if (!value || value.trim() === "") return true; // Allow empty
      return !isNaN(Number(value)) && Number(value) >= 0;
    }),
  }),
  patientLabTests: Yup.array(),
});

const NewPrescription = ({ appointment, onClose, showPatientHeading = true }) => {
  const user = useSelector((state) => state.user.user); // Access user from Redux
  const [complaints, setComplaints] = useState([]);
  const [diagnosis, setDiagnosis] = useState([]);
  const [labtests, setLabTests] = useState([]);
  const [medicine_list, setMedicine] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [patient_appointment, setPatientAppointment] = useState({});
  const [appointmentId, setAppointmentId] = useState({});
  const [showAdditionalComplaints, setShowAdditionalComplaints] = useState(false);
  const [showAdditionalDiagnoses, setShowAdditionalDiagnoses] = useState(false);
  // State for patient name and ID to display in heading
  const [displayPatientName, setDisplayPatientName] = useState("");
  const [displayPatientId, setDisplayPatientId] = useState("");
  const [patientDetails, setPatientDetails] = useState(null);
  const [hospitalName, setHospitalName] = useState(
    () => getOrganizationName(null) || "Hospital"
  );
  const [hospitalAddress, setHospitalAddress] = useState("");
  const [hospitalPhone, setHospitalPhone] = useState("");
  
  // Modal states for creating new medicine and lab test (kept for backward compatibility but not used)
  const [openCreateMedicineModal, setOpenCreateMedicineModal] = useState(false);
  const [openCreateLabTestModal, setOpenCreateLabTestModal] = useState(false);
  const [currentMedicineIndex, setCurrentMedicineIndex] = useState(null); // Track which medicine field is being edited
  
  // State for showing manual input fields
  const [showManualMedicineInput, setShowManualMedicineInput] = useState({}); // Object with medicine index as key
  const [showAdditionalLabTests, setShowAdditionalLabTests] = useState(false);
  const isSubmittingRef = useRef(false);
  const lastKnownAppointmentIdRef = useRef(null);
  const [frontDeskVitals, setFrontDeskVitals] = useState(null);
  const [successDialog, setSuccessDialog] = useState({
    open: false,
    message: "",
  });

  const showSuccessAndClose = (message) => {
    setSuccessDialog({
      open: true,
      message:
        message || "Prescription submitted successfully!",
    });
  };

  const handleSuccessDialogClose = async () => {
    setSuccessDialog({ open: false, message: "" });
    await onClose?.();
  };

  useEffect(() => {
    const apptId = resolveAppointmentIdFrom(
      appointment?.appointmentId,
      appointment?.appointment_id,
      appointment?.id,
      appointment
    );
    if (apptId != null) {
      lastKnownAppointmentIdRef.current = apptId;
    }
  }, [
    appointment?.id,
    appointment?.appointmentId,
    appointment?.appointment_id,
    appointment,
  ]);

  useEffect(() => {
    let cancelled = false;
    const loadFrontDeskVitals = async () => {
      const patientId = appointment?.patientId || appointment?.patient?.id;
      const apptId = resolveAppointmentIdFrom(
        appointment?.appointmentId,
        appointment?.appointment_id,
        appointment?.id,
        lastKnownAppointmentIdRef.current,
        appointment
      );
      if (!patientId && !apptId) return;
      try {
        const vitals = await getFrontDeskVitalSignsForVisit({
          patientId,
          appointmentId: apptId,
        });
        if (!cancelled && vitals) {
          setFrontDeskVitals(vitals);
        }
      } catch (error) {
        console.warn("[NewPrescription] Front-desk vital signs load skipped:", error);
      }
    };
    loadFrontDeskVitals();
    return () => {
      cancelled = true;
    };
  }, [appointment?.id, appointment?.appointmentId, appointment?.patientId]);

  useEffect(() => {
    let cancelled = false;
    const loadHospitalInfo = async () => {
      try {
        const organizationId = getOrganizationId(user);
        const name = getOrganizationName(null) || "Hospital";
        if (!cancelled) setHospitalName(name);
        if (!organizationId) return;
        const profile = await fetchOrganizationProfile(organizationId);
        if (cancelled) return;
        if (profile?.address) setHospitalAddress(profile.address);
        if (profile?.phone) setHospitalPhone(String(profile.phone));
      } catch (error) {
        console.warn("[NewPrescription] Hospital info load skipped:", error);
      }
    };
    loadHospitalInfo();
    return () => {
      cancelled = true;
    };
  }, [user]);

  const fetchComplaints = async () => {
    try {
      const data = await getComplainList();
      setComplaints(data);
    } catch (error) {
      console.log(error);
    }
  };
  const fetchDiagnosis = async () => {
    try {
      const data = await getDiagnosisList();
      setDiagnosis(data);
    } catch (error) {
      console.log(error);
    }
  };
  const fetchLabtests = async () => {
    try {
      // Fetch lab tests from base lab test table (not organization-specific)
      const data = await getAllLabTests();
      setLabTests(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error("[NewPrescription] Error fetching lab tests:", error);
      setLabTests([]); // Set empty array on error
    }
  };
  const fetchPatientAppointment = async () => {
    setFetching(true);
    try {
      // Log for debugging
      const storedUser = localStorage.getItem("user");
      let organizationId = null;
      if (storedUser) {
        try {
          const user = JSON.parse(storedUser);
          organizationId = user?.organizationId || localStorage.getItem("organizationId");
        } catch (e) {
          console.error("[NewPrescription] Error parsing stored user:", e);
        }
      }
      
      console.log(`[NewPrescription] Fetching previous prescriptions for patient ID: ${appointment.patientId}`);
      console.log(`[NewPrescription] Organization ID: ${organizationId}`);
      console.log(`[NewPrescription] apiClient will automatically add organizationId as query parameter`);
      
      const data = await getPrescriptionsByPatientId(appointment.patientId);
      
      console.log(`[NewPrescription] Received ${Array.isArray(data) ? data.length : 0} previous prescriptions`);
      
      const lastPrescription = getLatestPrescription(data);
      if (lastPrescription) {
        const prescriptionId = lastPrescription.id || lastPrescription.prescriptionId;
        console.log(`[NewPrescription] Selected latest prescription (id=${prescriptionId}) from ${data.length} records`);
        console.log(`[NewPrescription] Raw prescription data:`, lastPrescription);
        console.log(`[NewPrescription] Prescription ID: ${prescriptionId}`);
        console.log(`[NewPrescription] All prescription keys:`, Object.keys(lastPrescription));
        
        // Log all property values to see what's actually in the prescription
        Object.keys(lastPrescription).forEach(key => {
          const value = lastPrescription[key];
          if (value !== null && value !== undefined) {
            console.log(`[NewPrescription] Property "${key}":`, typeof value === 'object' ? JSON.stringify(value).substring(0, 200) : value);
          }
        });
        
        // Check if data is stored directly in prescription with different property names
        const checkProperty = (obj, possibleNames) => {
          for (const name of possibleNames) {
            if (obj[name] !== undefined && obj[name] !== null) {
              return obj[name];
            }
          }
          return null;
        };
        
        // Fetch complaints, diagnoses, and vital signs separately if they're not in the response
        let complaints = [];
        let diagnoses = [];
        let vitalSigns = [];
        
        // Check the original prescription data first (it already has the data)
        // This is the most reliable source since it comes from getPrescriptionsByPatientId
        console.log(`[NewPrescription] Checking original prescription for vital signs...`);
        console.log(`[NewPrescription] lastPrescription.vitalSigns exists:`, lastPrescription.vitalSigns !== undefined && lastPrescription.vitalSigns !== null);
        console.log(`[NewPrescription] lastPrescription.vitalSigns type:`, typeof lastPrescription.vitalSigns);
        console.log(`[NewPrescription] lastPrescription.vitalSigns is array:`, Array.isArray(lastPrescription.vitalSigns));
        if (lastPrescription.vitalSigns) {
          if (Array.isArray(lastPrescription.vitalSigns)) {
            console.log(`[NewPrescription] lastPrescription.vitalSigns.length:`, lastPrescription.vitalSigns.length);
            if (lastPrescription.vitalSigns.length > 0) {
              vitalSigns = lastPrescription.vitalSigns;
              console.log(`[NewPrescription] ✅ Found vital signs in original prescription data:`, vitalSigns);
              console.log(`[NewPrescription] First vital sign:`, JSON.stringify(vitalSigns[0]));
            } else {
              console.log(`[NewPrescription] ⚠️ lastPrescription.vitalSigns is an empty array`);
            }
          } else if (typeof lastPrescription.vitalSigns === 'object' && lastPrescription.vitalSigns !== null) {
            vitalSigns = [lastPrescription.vitalSigns];
            console.log(`[NewPrescription] ✅ Found vital signs in original prescription data (object):`, vitalSigns);
          } else {
            console.log(`[NewPrescription] ⚠️ lastPrescription.vitalSigns is not an array or object:`, lastPrescription.vitalSigns);
          }
        } else {
          console.log(`[NewPrescription] ⚠️ lastPrescription.vitalSigns is falsy`);
        }
        
        if (prescriptionId) {
          // Store original vital signs to prevent overwriting
          const originalVitalSigns = vitalSigns.length > 0 ? [...vitalSigns] : null;
          
          // First, try to get the full prescription by ID - it might include relations
          // But only use it if we don't already have the data from the original prescription
          if (complaints.length === 0 || diagnoses.length === 0 || vitalSigns.length === 0) {
            try {
              console.log(`[NewPrescription] Trying to fetch full prescription by ID ${prescriptionId}...`);
              const fullPrescription = await getPrescriptionById(prescriptionId);
              console.log(`[NewPrescription] Full prescription by ID:`, fullPrescription);
              if (fullPrescription) {
                // Check if full prescription has the data (only use if we don't already have it)
                if (complaints.length === 0 && Array.isArray(fullPrescription.complaints) && fullPrescription.complaints.length > 0) {
                  complaints = fullPrescription.complaints;
                  console.log(`[NewPrescription] Found complaints in full prescription:`, complaints);
                }
                if (diagnoses.length === 0 && Array.isArray(fullPrescription.diagnoses) && fullPrescription.diagnoses.length > 0) {
                  diagnoses = fullPrescription.diagnoses;
                  console.log(`[NewPrescription] Found diagnoses in full prescription:`, diagnoses);
                }
                // Only update vital signs if we don't already have them AND the fetched one is not empty
                if (vitalSigns.length === 0 && fullPrescription.vitalSigns) {
                  if (Array.isArray(fullPrescription.vitalSigns) && fullPrescription.vitalSigns.length > 0) {
                    vitalSigns = fullPrescription.vitalSigns;
                    console.log(`[NewPrescription] ✅ Found vital signs in full prescription:`, vitalSigns);
                  } else if (typeof fullPrescription.vitalSigns === 'object' && fullPrescription.vitalSigns !== null) {
                    vitalSigns = [fullPrescription.vitalSigns];
                    console.log(`[NewPrescription] ✅ Found vital signs in full prescription (object):`, vitalSigns);
                  } else {
                    console.log(`[NewPrescription] ⚠️ Full prescription vitalSigns is empty, keeping original:`, originalVitalSigns);
                    // Restore original if we had it
                    if (originalVitalSigns && originalVitalSigns.length > 0) {
                      vitalSigns = originalVitalSigns;
                    }
                  }
                } else if (vitalSigns.length > 0) {
                  console.log(`[NewPrescription] ✅ Already have vital signs, not overwriting with full prescription data`);
                }
              }
            } catch (error) {
              console.warn(`[NewPrescription] Error fetching full prescription by ID:`, error);
              // Restore original if we had it
              if (originalVitalSigns && originalVitalSigns.length > 0 && vitalSigns.length === 0) {
                vitalSigns = originalVitalSigns;
                console.log(`[NewPrescription] ✅ Restored original vital signs after error`);
              }
            }
          }
          
          // If we still don't have complaints, try the separate endpoint
          if (complaints.length === 0) {
            try {
              // Fetch complaints with explicit organizationId
              console.log(`[NewPrescription] Fetching complaints for prescription ${prescriptionId}...`);
              const complaintsData = await getComplaintsByPrescriptionId(prescriptionId);
              console.log(`[NewPrescription] Raw complaints response:`, complaintsData);
              console.log(`[NewPrescription] Complaints response type:`, typeof complaintsData);
              console.log(`[NewPrescription] Complaints is array:`, Array.isArray(complaintsData));
              
              if (Array.isArray(complaintsData)) {
                if (complaintsData.length > 0) {
                  console.log(`[NewPrescription] First complaint item:`, complaintsData[0]);
                  console.log(`[NewPrescription] First complaint item keys:`, complaintsData[0] ? Object.keys(complaintsData[0]) : 'N/A');
                }
                complaints = complaintsData.map(c => {
                  if (typeof c === 'string') return c;
                  return c.complaint || c.complaintName || c.name || c.value || JSON.stringify(c);
                }).filter(Boolean);
              } else if (typeof complaintsData === 'string') {
                // Try to parse if it's a JSON string
                try {
                  const parsed = JSON.parse(complaintsData);
                  if (Array.isArray(parsed)) {
                    complaints = parsed;
                  } else {
                    complaints = [complaintsData];
                  }
                } catch {
                  complaints = [complaintsData];
                }
              } else if (complaintsData && typeof complaintsData === 'object') {
                // If it's an object, try to extract array from common properties
                if (Array.isArray(complaintsData.data)) {
                  complaints = complaintsData.data.map(c => c.complaint || c.complaintName || c.name || c).filter(Boolean);
                } else if (Array.isArray(complaintsData.complaints)) {
                  complaints = complaintsData.complaints.map(c => c.complaint || c.complaintName || c.name || c).filter(Boolean);
                }
              }
              console.log(`[NewPrescription] Processed complaints:`, complaints);
            } catch (error) {
              console.warn(`[NewPrescription] Error fetching complaints:`, error);
              console.warn(`[NewPrescription] Error details:`, error.response?.data, error.response?.status);
            }
          }
          
          // If we still don't have diagnoses, try the separate endpoint
          if (diagnoses.length === 0) {
            try {
              // Fetch diagnoses - try common endpoint patterns with explicit organizationId
              console.log(`[NewPrescription] Fetching diagnoses for prescription ${prescriptionId}...`);
              const storedUser = localStorage.getItem("user");
              let organizationId = null;
              if (storedUser) {
                try {
                  const user = JSON.parse(storedUser);
                  organizationId = user?.organizationId || localStorage.getItem("organizationId");
                  if (organizationId && typeof organizationId === 'string') {
                    organizationId = parseInt(organizationId, 10);
                  }
                } catch (e) {
                  // ignore
                }
              }
              
              try {
                const diagnosesData = await apiClient.get(`/prescription-diagnoses/by-prescription/${prescriptionId}`, {
                  params: organizationId && organizationId > 0 ? { organizationId } : {}
                });
                console.log(`[NewPrescription] Raw diagnoses response:`, diagnosesData.data);
                if (Array.isArray(diagnosesData.data)) {
                  diagnoses = diagnosesData.data.map(d => {
                    if (typeof d === 'string') return d;
                    return d.diagnosis || d.diagnosisName || d.name || d.value || JSON.stringify(d);
                  }).filter(Boolean);
                } else if (typeof diagnosesData.data === 'string') {
                  try {
                    const parsed = JSON.parse(diagnosesData.data);
                    diagnoses = Array.isArray(parsed) ? parsed : [diagnosesData.data];
                  } catch {
                    diagnoses = [diagnosesData.data];
                  }
                }
              } catch (err1) {
                console.warn(`[NewPrescription] First diagnoses endpoint failed:`, err1.response?.status, err1.response?.data);
                // Try alternative endpoint
                try {
                  const diagnosesData = await apiClient.get(`/prescription-diagnosis/by-prescription/${prescriptionId}`, {
                    params: organizationId && organizationId > 0 ? { organizationId } : {}
                  });
                  console.log(`[NewPrescription] Raw diagnoses response (alt):`, diagnosesData.data);
                  if (Array.isArray(diagnosesData.data)) {
                    diagnoses = diagnosesData.data.map(d => {
                      if (typeof d === 'string') return d;
                      return d.diagnosis || d.diagnosisName || d.name || d.value || JSON.stringify(d);
                    }).filter(Boolean);
                  }
                } catch (err2) {
                  console.warn(`[NewPrescription] Alternative diagnoses endpoint also failed:`, err2.response?.status, err2.response?.data);
                }
              }
              console.log(`[NewPrescription] Processed diagnoses:`, diagnoses);
            } catch (error) {
              console.warn(`[NewPrescription] Error fetching diagnoses:`, error);
              console.warn(`[NewPrescription] Error details:`, error.response?.data, error.response?.status);
            }
          }
          
          // If we still don't have vital signs, try the separate endpoint
          if (vitalSigns.length === 0) {
            try {
              // Fetch vital signs - try common endpoint patterns with explicit organizationId
              console.log(`[NewPrescription] Fetching vital signs for prescription ${prescriptionId}...`);
              const storedUser = localStorage.getItem("user");
              let organizationId = null;
              if (storedUser) {
                try {
                  const user = JSON.parse(storedUser);
                  organizationId = user?.organizationId || localStorage.getItem("organizationId");
                  if (organizationId && typeof organizationId === 'string') {
                    organizationId = parseInt(organizationId, 10);
                  }
                } catch (e) {
                  // ignore
                }
              }
              
              try {
                const vitalSignsData = await apiClient.get(`/prescription-vitalsigns/by-prescription/${prescriptionId}`, {
                  params: organizationId && organizationId > 0 ? { organizationId } : {}
                });
                console.log(`[NewPrescription] Raw vital signs response:`, vitalSignsData.data);
                if (Array.isArray(vitalSignsData.data) && vitalSignsData.data.length > 0) {
                  vitalSigns = vitalSignsData.data;
                } else if (vitalSignsData.data && typeof vitalSignsData.data === 'object') {
                  vitalSigns = [vitalSignsData.data];
                } else if (typeof vitalSignsData.data === 'string') {
                  try {
                    const parsed = JSON.parse(vitalSignsData.data);
                    vitalSigns = Array.isArray(parsed) ? parsed : [parsed];
                  } catch {
                    // Not JSON, skip
                  }
                }
              } catch (err1) {
                console.warn(`[NewPrescription] First vital signs endpoint failed:`, err1.response?.status, err1.response?.data);
                // Try alternative endpoint
                try {
                  const vitalSignsData = await apiClient.get(`/prescription-vitals/by-prescription/${prescriptionId}`, {
                    params: organizationId && organizationId > 0 ? { organizationId } : {}
                  });
                  console.log(`[NewPrescription] Raw vital signs response (alt):`, vitalSignsData.data);
                  if (Array.isArray(vitalSignsData.data) && vitalSignsData.data.length > 0) {
                    vitalSigns = vitalSignsData.data;
                  } else if (vitalSignsData.data && typeof vitalSignsData.data === 'object') {
                    vitalSigns = [vitalSignsData.data];
                  }
                } catch (err2) {
                  console.warn(`[NewPrescription] Alternative vital signs endpoint also failed:`, err2.response?.status, err2.response?.data);
                  // Try getting from prescription directly if it has vitalSigns
                  const vitalSignsFromPrescription = checkProperty(lastPrescription, [
                    'vitalSigns', 'vital_signs', 'vitals', 'vitalSignsData', 'vitalSignsList'
                  ]);
                  if (vitalSignsFromPrescription) {
                    if (Array.isArray(vitalSignsFromPrescription)) {
                      vitalSigns = vitalSignsFromPrescription;
                    } else if (typeof vitalSignsFromPrescription === 'object') {
                      vitalSigns = [vitalSignsFromPrescription];
                    } else if (typeof vitalSignsFromPrescription === 'string') {
                      try {
                        const parsed = JSON.parse(vitalSignsFromPrescription);
                        vitalSigns = Array.isArray(parsed) ? parsed : [parsed];
                      } catch {
                        // Not JSON, skip
                      }
                    }
                    console.log(`[NewPrescription] Using vital signs from prescription data:`, vitalSigns);
                  }
                }
              }
              console.log(`[NewPrescription] Processed vital signs:`, vitalSigns);
            } catch (error) {
              console.warn(`[NewPrescription] Error fetching vital signs:`, error);
              console.warn(`[NewPrescription] Error details:`, error.response?.data, error.response?.status);
              // Fallback to prescription data if available
              const vitalSignsFromPrescription = checkProperty(lastPrescription, [
                'vitalSigns', 'vital_signs', 'vitals', 'vitalSignsData', 'vitalSignsList'
              ]);
              if (vitalSignsFromPrescription) {
                if (Array.isArray(vitalSignsFromPrescription)) {
                  vitalSigns = vitalSignsFromPrescription;
                } else if (typeof vitalSignsFromPrescription === 'object') {
                  vitalSigns = [vitalSignsFromPrescription];
                }
              }
            }
          }
        }
        
        // Check prescription object for alternative property names
        const complaintsFromPrescription = checkProperty(lastPrescription, [
          'complaints', 'complaint', 'complaintsList', 'complaintsData', 'complaintList'
        ]);
        const diagnosesFromPrescription = checkProperty(lastPrescription, [
          'diagnoses', 'diagnosis', 'diagnosesList', 'diagnosesData', 'diagnosisList'
        ]);
        const vitalSignsFromPrescription = checkProperty(lastPrescription, [
          'vitalSigns', 'vital_signs', 'vitals', 'vitalSignsData', 'vitalSignsList'
        ]);
        
        console.log(`[NewPrescription] Complaints from prescription:`, complaintsFromPrescription);
        console.log(`[NewPrescription] Diagnoses from prescription:`, diagnosesFromPrescription);
        console.log(`[NewPrescription] Vital signs from prescription:`, vitalSignsFromPrescription);
        
        // Helper to parse and normalize data
        const normalizeArrayData = (data) => {
          if (!data) return [];
          // If it's a string, try to parse as JSON
          if (typeof data === 'string') {
            try {
              const parsed = JSON.parse(data);
              return Array.isArray(parsed) ? parsed : [parsed];
            } catch {
              return [data];
            }
          }
          // If it's already an array
          if (Array.isArray(data)) {
            return data.map(item => {
              if (typeof item === 'string') return item;
              return item.complaint || item.complaintName || item.diagnosis || item.diagnosisName || item.name || item.value || JSON.stringify(item);
            }).filter(Boolean);
          }
          // If it's an object, wrap in array
          if (typeof data === 'object') {
            return [data];
          }
          return [];
        };
        
        // Normalize the prescription data to ensure it's in the expected format
        const normalizedPrescription = {
          ...lastPrescription,
          // Use fetched complaints or fallback to prescription data
          complaints: complaints.length > 0 ? complaints : normalizeArrayData(complaintsFromPrescription),
          // Use fetched diagnoses or fallback to prescription data
          diagnoses: diagnoses.length > 0 ? diagnoses : normalizeArrayData(diagnosesFromPrescription),
          // Use fetched vital signs or fallback to prescription data
          // Priority: vitalSignsFromPrescription (original data) > fetched vitalSigns (if not empty) > empty array
          vitalSigns: (() => {
            console.log(`[NewPrescription] ========== NORMALIZING VITAL SIGNS ==========`);
            console.log(`[NewPrescription] Fetched vitalSigns.length:`, vitalSigns.length);
            console.log(`[NewPrescription] Fetched vitalSigns:`, vitalSigns);
            console.log(`[NewPrescription] vitalSignsFromPrescription:`, vitalSignsFromPrescription);
            console.log(`[NewPrescription] vitalSignsFromPrescription type:`, typeof vitalSignsFromPrescription);
            console.log(`[NewPrescription] vitalSignsFromPrescription is array:`, Array.isArray(vitalSignsFromPrescription));
            if (vitalSignsFromPrescription && Array.isArray(vitalSignsFromPrescription)) {
              console.log(`[NewPrescription] vitalSignsFromPrescription length:`, vitalSignsFromPrescription.length);
            }
            
            // PRIORITY 1: Use vital signs from original prescription (most reliable)
            if (vitalSignsFromPrescription) {
              if (Array.isArray(vitalSignsFromPrescription) && vitalSignsFromPrescription.length > 0) {
                console.log(`[NewPrescription] ✅ Using vital signs from original prescription (array):`, vitalSignsFromPrescription);
                return vitalSignsFromPrescription;
              }
              if (typeof vitalSignsFromPrescription === 'string' && vitalSignsFromPrescription.trim().length > 0) {
                try {
                  const parsed = JSON.parse(vitalSignsFromPrescription);
                  const result = Array.isArray(parsed) ? parsed : [parsed];
                  console.log(`[NewPrescription] ✅ Using vital signs from prescription (parsed string):`, result);
                  return result;
                } catch {
                  console.log(`[NewPrescription] ⚠️ Failed to parse vital signs string`);
                }
              }
              if (typeof vitalSignsFromPrescription === 'object' && vitalSignsFromPrescription !== null) {
                const result = [vitalSignsFromPrescription];
                console.log(`[NewPrescription] ✅ Using vital signs from prescription (object):`, result);
                return result;
              }
            }
            
            // PRIORITY 2: Use fetched vital signs if we have them
            if (vitalSigns.length > 0) {
              console.log(`[NewPrescription] ✅ Using fetched vital signs:`, vitalSigns);
              return vitalSigns;
            }
            
            console.log(`[NewPrescription] ⚠️ No vital signs found, returning empty array`);
            console.log(`[NewPrescription] ===========================================`);
            return [];
          })(),
          // Ensure medicines is an array
          medicines: Array.isArray(lastPrescription.medicines) 
            ? lastPrescription.medicines 
            : (Array.isArray(lastPrescription.medicine) ? lastPrescription.medicine : [])
        };
        
        const latestVitalSign = getLatestVitalSign(normalizedPrescription.vitalSigns);
        normalizedPrescription.vitalSigns = latestVitalSign ? [latestVitalSign] : [];
        
        console.log(`[NewPrescription] Normalized prescription data:`, normalizedPrescription);
        console.log(`[NewPrescription] Complaints:`, normalizedPrescription.complaints);
        console.log(`[NewPrescription] Diagnoses:`, normalizedPrescription.diagnoses);
        console.log(`[NewPrescription] Vital Signs:`, normalizedPrescription.vitalSigns);
        console.log(`[NewPrescription] Medicines:`, normalizedPrescription.medicines);
        
        setPatientAppointment(normalizedPrescription);
      } else {
        console.log(`[NewPrescription] No previous prescriptions found for patient ${appointment.patientId} in organization ${organizationId}`);
        setPatientAppointment({}); // Set empty object if no data
      }
    } catch (error) {
      console.error("[NewPrescription] Error fetching previous prescriptions:", error);
      console.error("[NewPrescription] Error response:", error.response?.data);
      console.error("[NewPrescription] Error status:", error.response?.status);
      setPatientAppointment({}); // Set empty object on error
    }
    setFetching(false);
  };

  const fetchMedicine = async () => {
    try {
      // Fetch medicines from base raw medicine table (not organization-specific)
      const data = await getAllMedicine();
      // Transform the data to match the expected format (medicineName -> name, id -> medicineId)
      const transformedData = Array.isArray(data) ? data.map(medicine => ({
        id: medicine.id,
        medicineId: medicine.id,
        name: medicine.medicineName || medicine.name || '',
        medicineName: medicine.medicineName || medicine.name || '',
        medicineType: medicine.medicineType || '',
        manufacturer: medicine.manufacturer || '',
        price: medicine.price || 0,
        costPerUnit: medicine.costPerUnit || 0,
      })) : [];
      setMedicine(transformedData);
    } catch (error) {
      console.error("[NewPrescription] Error fetching medicines:", error);
      setMedicine([]); // Set empty array on error
    }
  };


  // Function to get distinct medicines
  const getDistinctMedicines = (medicines) => {
    const seen = new Set();
    return medicines.filter((medicine) => {
      const medicineName = medicine.name || medicine.medicineName || '';
      const duplicate = seen.has(medicineName);
      seen.add(medicineName);
      return !duplicate;
    });
  };

  // Function to save manual medicine entry to base table
  const saveManualMedicine = async (medicineName, setFieldValue, index) => {
    try {
      if (!medicineName || !medicineName.trim()) {
        return null;
      }

      // Check if medicine already exists
      const existingMedicine = medicine_list.find(
        (med) => (med.name || med.medicineName || '').toLowerCase().trim() === medicineName.toLowerCase().trim()
      );

      if (existingMedicine) {
        // Medicine already exists, use its ID
        setFieldValue(`medicines.${index}.name`, existingMedicine.name || existingMedicine.medicineName);
        setFieldValue(`medicines.${index}.medicineId`, existingMedicine.id || existingMedicine.medicineId);
        return existingMedicine.id || existingMedicine.medicineId;
      }

      // Create new medicine in base table with minimal required fields
      const medicineData = {
        medicineName: medicineName.trim(),
        medicineType: "Homeopathy", // Default type
        manufacturer: "Unknown", // Default manufacturer
        supplier: "Unknown", // Default supplier
        details: 1, // Default details
        price: 0, // Default price
        costPerUnit: 0, // Default cost per unit
        medicineDetails: "tablet", // Default details
        userId: user?.id || 1, // User ID from Redux
      };

      console.log(`[NewPrescription] Creating medicine "${medicineName}" in base table...`);
      const createdMedicine = await createNewMedicine(medicineData);
      console.log(`[NewPrescription] ✅ Created medicine "${medicineName}" with ID: ${createdMedicine.id}`);

      // Update the medicine list and form field
      setFieldValue(`medicines.${index}.name`, createdMedicine.medicineName || medicineName.trim());
      setFieldValue(`medicines.${index}.medicineId`, createdMedicine.id);

      // Refresh medicine list to include the new medicine
      await fetchMedicine();

      return createdMedicine.id;
    } catch (error) {
      console.error(`[NewPrescription] Error saving manual medicine "${medicineName}":`, error);
      alert(`Failed to save medicine "${medicineName}". Please try again or select from the list.`);
      return null;
    }
  };

  // Function to save manual lab test entries to base table
  const saveManualLabTests = async (labTestNames) => {
    try {
      if (!labTestNames || labTestNames.length === 0) {
        return [];
      }

      const savedLabTests = [];

      for (const testName of labTestNames) {
        if (!testName || !testName.trim()) continue;

        // Check if lab test already exists
        const existingTest = labtests.find(
          (test) => (test.name || '').toLowerCase().trim() === testName.toLowerCase().trim()
        );

        if (existingTest) {
          savedLabTests.push({ name: existingTest.name, id: existingTest.id });
          continue;
        }

        // Create new lab test in base table with minimal required fields
        const labTestData = {
          name: testName.trim(),
          amount: 0, // Default amount
        };

        console.log(`[NewPrescription] Creating lab test "${testName}" in base table...`);
        const createdTest = await createLabTestInBaseTable(labTestData);
        console.log(`[NewPrescription] ✅ Created lab test "${testName}" with ID: ${createdTest.id}`);

        savedLabTests.push({ name: createdTest.name || testName.trim(), id: createdTest.id });
      }

      // Refresh lab test list to include new tests
      if (savedLabTests.length > 0) {
        await fetchLabtests();
      }

      return savedLabTests;
    } catch (error) {
      console.error(`[NewPrescription] Error saving manual lab tests:`, error);
      alert(`Failed to save some lab tests. Please try again or select from the list.`);
      return [];
    }
  };

  // Fetch patient details if name or ID is missing
  useEffect(() => {
    const fetchPatientDetails = async () => {
      if (!appointment) {
        console.warn(`[NewPrescription] No appointment object provided`);
        setDisplayPatientName("");
        setDisplayPatientId("");
        return;
      }
      
      console.log(`[NewPrescription] ========== FETCHING PATIENT DETAILS ==========`);
      console.log(`[NewPrescription] Appointment object:`, appointment);
      console.log(`[NewPrescription] Appointment keys:`, Object.keys(appointment || {}));
      
      // Try to get patient name and ID from appointment object first (check multiple possible property names)
      const patientName = appointment?.patientName || 
                         appointment?.patient?.name || 
                         appointment?.name || 
                         "";
      const patientId = appointment?.patientId || 
                       appointment?.patient?.id || 
                       appointment?.id || 
                       "";
      
      console.log(`[NewPrescription] Initial patientName from appointment:`, patientName);
      console.log(`[NewPrescription] Initial patientId from appointment:`, patientId);
      console.log(`[NewPrescription] patientName type:`, typeof patientName, `value:`, patientName);
      console.log(`[NewPrescription] patientId type:`, typeof patientId, `value:`, patientId);
      
      // Set initial values immediately
      if (patientName && patientName.trim() !== "") {
        const trimmedName = patientName.trim();
        setDisplayPatientName(trimmedName);
        console.log(`[NewPrescription] ✅ Set displayPatientName to: ${trimmedName}`);
      } else {
        setDisplayPatientName("");
        console.log(`[NewPrescription] ⚠️ patientName is empty, set displayPatientName to empty string`);
      }
      
      if (patientId) {
        const patientIdStr = String(patientId);
        setDisplayPatientId(patientIdStr);
        console.log(`[NewPrescription] ✅ Set displayPatientId to: ${patientIdStr}`);
      } else {
        setDisplayPatientId("");
        console.log(`[NewPrescription] ⚠️ patientId is empty, set displayPatientId to empty string`);
      }
      
      // Always fetch patient details for print demographics (and name if missing)
      if (patientId) {
        try {
          console.log(`[NewPrescription] Fetching patient details for patientId: ${patientId}`);
          
          // Get organizationId to ensure we're querying the right table
          const storedUser = localStorage.getItem("user");
          let organizationId = null;
          if (storedUser) {
            try {
              const user = JSON.parse(storedUser);
              organizationId = user?.organizationId || localStorage.getItem("organizationId");
              console.log(`[NewPrescription] Organization ID: ${organizationId}`);
            } catch (e) {
              console.error(`[NewPrescription] Error parsing stored user:`, e);
            }
          }
          
          // Fetch patient from organization-specific table (apiClient will automatically add organizationId)
          const patientData = await getPatientById(patientId);
          console.log(`[NewPrescription] Fetched patient data:`, patientData);
          
          if (patientData) {
            setPatientDetails(patientData);

            // Try multiple possible property names for patient name
            let fetchedName = patientData.name || 
                             patientData.patientName || 
                             "";
            
            // If name is still empty, try combining firstName and lastName
            if (!fetchedName || fetchedName.trim() === "") {
              const firstName = patientData.firstName || "";
              const lastName = patientData.lastName || "";
              const combined = `${firstName} ${lastName}`.trim();
              if (combined) {
                fetchedName = combined;
              }
            }
            
            // Try multiple possible property names for patient ID
            const fetchedId = patientData.id || 
                            patientData.patientId || 
                            patientId;
            
            console.log(`[NewPrescription] Extracted patient name: ${fetchedName}`);
            console.log(`[NewPrescription] Extracted patient ID: ${fetchedId}`);
            
            if ((!patientName || patientName.trim() === "") && fetchedName && fetchedName.trim() !== "") {
              setDisplayPatientName(fetchedName.trim());
              console.log(`[NewPrescription] ✅ Set displayPatientName to: ${fetchedName.trim()}`);
            }
            if (fetchedId) {
              setDisplayPatientId(String(fetchedId));
              console.log(`[NewPrescription] ✅ Set displayPatientId to: ${fetchedId}`);
            }
          } else {
            console.warn(`[NewPrescription] ⚠️ Patient data is null or undefined`);
            // Keep the patientId even if fetch returns null
            if (patientId) {
              setDisplayPatientId(String(patientId));
            }
          }
        } catch (error) {
          console.error(`[NewPrescription] ❌ Error fetching patient details:`, error);
          console.error(`[NewPrescription] Error response:`, error.response?.data);
          console.error(`[NewPrescription] Error status:`, error.response?.status);
          
          // Keep the patientId even if fetch fails
          if (patientId) {
            setDisplayPatientId(String(patientId));
            console.log(`[NewPrescription] Kept patientId: ${patientId} after fetch error`);
          }
        }
      } else if (patientName && patientName.trim() !== "") {
        console.log(`[NewPrescription] ✅ Patient name already available from appointment: ${patientName}`);
      } else {
        console.warn(`[NewPrescription] ⚠️ No patientId available to fetch patient details`);
      }
      
      console.log(`[NewPrescription] ===========================================`);
    };
    
    fetchPatientDetails();
  }, [appointment]);

  useEffect(() => {
    if (!appointment || !appointment.patientId) {
      console.warn("[NewPrescription] Appointment or patientId is missing, skipping fetch");
      return;
    }
    
    Promise.all([
      fetchComplaints(),
      fetchDiagnosis(),
      fetchLabtests(),
      fetchMedicine(),
      fetchPatientAppointment(),
    ]);
  }, [appointment?.patientId]); // Refetch when patientId changes

  const initialValues = {
    patientId: resolvePositiveId(
      appointment?.patientId,
      appointment?.patient_id,
      appointment?.patient?.id
    ),
    doctorId: resolvePositiveId(
      appointment?.doctorId,
      appointment?.doctor_id,
      appointment?.doctor?.id
    ),
    appointmentId: resolveAppointmentIdFrom(
      appointment?.appointmentId,
      appointment?.appointment_id,
      appointment?.id,
      lastKnownAppointmentIdRef.current,
      appointment
    ),
    medicines: [
      {
        medicineId: null, // Add medicineId field
        name: "",
        dosage: "",
        frequency: "",
        duration: "",
        notes: "", // Notes field for each medicine
      },
    ],
    medicineNotes: "", // Common notes field for all medicines (UI only)
    nextVisitDate: "",
    isExtraMedicine: false,
    numberOfDays: 0,
    complaints: [],
    diagnoses: [],
    additionalComplaints: "",
    additionalDiagnoses: "",
    additionalLabTests: "",
    vitalSigns: {
      bpReading: "",
      weightReading: "",
      heightReading: "",
      oxygenSaturation: "",
      pulseRate: "",
      respirationRate: "",
    },
    patientLabTests: [],
  };

  const handleSubmit = async (
    values,
    { setSubmitting, validateForm, setFieldTouched, setFieldValue },
    { skipGuard = false } = {}
  ) => {
    if (!skipGuard) {
      if (isSubmittingRef.current) return;
      isSubmittingRef.current = true;
      setSubmitting(true);
    }

    try {
    // Sanitize vitals before validate so prefilled front-desk values with units
    // (e.g. "98%", "70 kg") do not silently block submit.
    const sanitizedVitals = sanitizeVitalSignsForSubmit(values.vitalSigns || {});
    const valuesForSubmit = {
      ...values,
      vitalSigns: sanitizedVitals,
    };
    if (typeof setFieldValue === "function") {
      Object.entries(sanitizedVitals).forEach(([key, val]) => {
        if (String(values?.vitalSigns?.[key] ?? "") !== String(val ?? "")) {
          setFieldValue(`vitalSigns.${key}`, val);
        }
      });
    }

    // Validate form before submission
    const validationErrors = await validateForm(valuesForSubmit);
    
    // Check if there are any validation errors — show field messages + toast
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      touchValidationErrors(validationErrors, setFieldTouched);
      const messages = collectValidationMessages(validationErrors);
      toast.error(
        messages[0] ||
          "Please fill required prescription fields before submitting.",
        { position: "bottom-center", autoClose: 4000 }
      );
      return; // Don't proceed with submission
    }
   
    // Ensure all manual medicines are saved and have IDs before processing
    const medicinesWithIds = [];
    for (let i = 0; i < valuesForSubmit.medicines.length; i++) {
      const medicine = valuesForSubmit.medicines[i];
      if (medicine.name && medicine.name.trim()) {
        let medicineId = medicine.medicineId;
        
        // If medicine doesn't have an ID, save it to base table
        if (!medicineId) {
          console.log(`[NewPrescription] Saving manual medicine "${medicine.name}" before submission...`);
          
          // Check if medicine already exists
          const existingMedicine = medicine_list.find(
            (med) => (med.name || med.medicineName || '').toLowerCase().trim() === medicine.name.toLowerCase().trim()
          );
          
          if (existingMedicine) {
            medicineId = existingMedicine.id || existingMedicine.medicineId;
          } else {
            // Create new medicine in base table
            try {
              const medicineData = {
                medicineName: medicine.name.trim(),
                medicineType: "Homeopathy",
                manufacturer: "Unknown",
                supplier: "Unknown",
                details: 1,
                price: 0,
                costPerUnit: 0,
                medicineDetails: "tablet",
                userId: user?.id || 1,
              };
              
              const createdMedicine = await createNewMedicine(medicineData);
              medicineId = createdMedicine.id;
              console.log(`[NewPrescription] ✅ Created medicine "${medicine.name}" with ID: ${medicineId}`);
              
              // Refresh medicine list
              await fetchMedicine();
            } catch (error) {
              console.error(`[NewPrescription] Error saving medicine "${medicine.name}":`, error);
              toast.error(`Failed to save medicine "${medicine.name}". Please try again.`);
              return; // Stop submission if medicine save failed
            }
          }
        }
        
        const medicineIdNum = Number(medicineId);
        if (!Number.isFinite(medicineIdNum) || medicineIdNum <= 0) {
          toast.error(
            `Medicine "${medicine.name}" is missing a valid ID. Please reselect it and try again.`
          );
          return;
        }

        medicinesWithIds.push({
          ...medicine,
          medicineId: medicineIdNum,
        });
      }
    }
    
    // Copy the common notes to each medicine object — keep only backend fields
    // Each medicine keeps its own comment now — it no longer falls back to the
    // shared "medicine notes" field, which is sent separately as prescription-level
    // advice (see final_data.advice below) so the two aren't conflated.
    const medicinesWithNotes = medicinesWithIds.map((medicine) => ({
      medicineId: medicine.medicineId,
      dosage: medicine.dosage || "",
      frequency: medicine.frequency || "",
      duration: medicine.duration || "",
      notes: medicine.notes || "",
    }));
    
    // Process additional complaints - split by newline or comma and trim
    const additionalComplaintsArray = valuesForSubmit.additionalComplaints
      ? valuesForSubmit.additionalComplaints
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Process additional diagnoses - split by newline or comma and trim
    const additionalDiagnosesArray = valuesForSubmit.additionalDiagnoses
      ? valuesForSubmit.additionalDiagnoses
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Merge selected and additional complaints/diagnoses, removing duplicates
    const allComplaints = [...new Set([...(valuesForSubmit.complaints || []), ...additionalComplaintsArray])];
    const allDiagnoses = [...new Set([...(valuesForSubmit.diagnoses || []), ...additionalDiagnosesArray])];
    
    // Ensure patientId is valid - use the appointment's patientId
    // The appointment should already have the correct patientId from the organization-specific table
    const patientId = resolvePositiveId(
      appointment?.patientId,
      appointment?.patient_id,
      appointment?.patient?.id,
      valuesForSubmit.patientId
    );
    
    if (!patientId) {
      toast.error("Patient ID is missing. Please reopen the appointment and try again.");
      return;
    }

    const doctorId = resolvePositiveId(
      valuesForSubmit.doctorId,
      appointment?.doctorId,
      appointment?.doctor_id,
      appointment?.doctor?.id
    );
    if (!doctorId) {
      toast.error("Doctor ID is missing. Please reopen the appointment and try again.");
      return;
    }

    const appointmentIdResolved = resolveAppointmentIdFrom(
      valuesForSubmit.appointmentId,
      appointment?.appointmentId,
      appointment?.appointment_id,
      appointment?.id,
      lastKnownAppointmentIdRef.current,
      appointment
    );
    if (appointmentIdResolved != null) {
      lastKnownAppointmentIdRef.current = appointmentIdResolved;
    }
    if (!appointmentIdResolved) {
      toast.error(
        "Appointment ID is missing. Please reopen the called appointment and try again."
      );
      return;
    }
    
    // Process additional lab tests - split by newline or comma and trim
    const additionalLabTestsArray = valuesForSubmit.additionalLabTests
      ? valuesForSubmit.additionalLabTests
          .split(/\n|,/)
          .map(item => item.trim())
          .filter(item => item.length > 0)
      : [];
    
    // Save manual lab tests to base table and get their IDs
    let allLabTestNames = [...(valuesForSubmit.patientLabTests || [])];
    if (additionalLabTestsArray.length > 0) {
      console.log(`[NewPrescription] Saving ${additionalLabTestsArray.length} manual lab tests before submission...`);
      const savedLabTests = await saveManualLabTests(additionalLabTestsArray);
      
      // Refresh lab tests list to include newly saved tests
      await fetchLabtests();
      
      // Add saved lab test names to the selected list
      const savedTestNames = savedLabTests.map(test => test.name);
      allLabTestNames = [...new Set([...allLabTestNames, ...savedTestNames])];
    }
    
    // Prepare lab tests - include all lab tests (both selected and manually entered) with their IDs
    // Refresh labtests array to ensure we have the latest data including newly saved tests
    let currentLabTestsList = [];
    try {
      currentLabTestsList = await getAllLabTests();
    } catch (labListError) {
      console.error("[NewPrescription] Failed to load lab tests list:", labListError);
      currentLabTestsList = Array.isArray(labtests) ? labtests : [];
    }
    const selectedLabTests = currentLabTestsList
      .filter((test) => allLabTestNames.includes(test.name) && Number(test.id) > 0)
      .map((test) => ({
        labTestId: Number(test.id),
      }));
    
    // Get organizationId to ensure it's included
    const storedUser = localStorage.getItem("user");
    let organizationId = null;
    if (storedUser) {
      try {
        const userObj = JSON.parse(storedUser);
        organizationId = userObj?.organizationId || localStorage.getItem("organizationId");
        if (organizationId && typeof organizationId === 'string') {
          organizationId = parseInt(organizationId, 10);
        }
      } catch (e) {
        console.error("[NewPrescription] Error parsing stored user:", e);
      }
    }
    
    const vitalSignsArray = buildVitalSignsPayload(sanitizedVitals);
    
    // Ensure complaints and diagnoses are properly formatted
    // Backend may expect arrays of strings OR arrays of objects with complaint/diagnosis properties
    // We'll send as strings first (current format), backend should handle conversion if needed
    const formattedComplaints = Array.isArray(allComplaints) && allComplaints.length > 0 
      ? allComplaints
          .filter(c => c && (typeof c === 'string' ? c.trim().length > 0 : true))
          .map(c => typeof c === 'string' ? c.trim() : (c.complaint || c.complaintName || c.name || c))
      : [];
    const formattedDiagnoses = Array.isArray(allDiagnoses) && allDiagnoses.length > 0 
      ? allDiagnoses
          .filter(d => d && (typeof d === 'string' ? d.trim().length > 0 : true))
          .map(d => typeof d === 'string' ? d.trim() : (d.diagnosis || d.diagnosisName || d.name || d))
      : [];
    
    const final_data = {
      patientId: patientId,
      doctorId: doctorId,
      appointmentId: appointmentIdResolved,
      appointment_id: appointmentIdResolved,
      complaints: formattedComplaints,
      diagnoses: formattedDiagnoses,
      medicines: medicinesWithNotes,
      vitalSigns: vitalSignsArray,
      patientLabTests: selectedLabTests,
      nextVisitDate: valuesForSubmit.nextVisitDate || null,
      isExtraMedicine: valuesForSubmit.isExtraMedicine || false,
      numberOfDays: valuesForSubmit.numberOfDays || 0,
      deductInventory: false,
      // Prescription-level "advice given" — printed separately from each
      // medicine's own comment. Requires a new `advice` column on the
      // prescription table (backend change, not yet made).
      advice: valuesForSubmit.medicineNotes || null,
      ...(organizationId && organizationId > 0 ? { organizationId } : {}),
    };
    
    // Remove medicineNotes and additional fields from the request payload (they're already processed)
    // Don't include them in final_data
    
    console.log('[NewPrescription] ========== SUBMITTING PRESCRIPTION ==========');
    console.log('[NewPrescription] Patient ID:', patientId);
    console.log('[NewPrescription] Appointment ID:', appointmentIdResolved);
    console.log('[NewPrescription] Organization ID:', organizationId);
    console.log('[NewPrescription] Complaints count:', formattedComplaints.length);
    console.log('[NewPrescription] Complaints:', formattedComplaints);
    console.log('[NewPrescription] Diagnoses count:', formattedDiagnoses.length);
    console.log('[NewPrescription] Diagnoses:', formattedDiagnoses);
    console.log('[NewPrescription] Vital Signs:', vitalSignsArray);
    console.log('[NewPrescription] Medicines count:', medicinesWithNotes.length);
    console.log('[NewPrescription] Lab Tests count:', selectedLabTests.length);
    console.log('[NewPrescription] Full prescription data:', JSON.stringify(final_data, null, 2));
    console.log('[NewPrescription] ============================================');
    
    try {
      const data = await createPrescription(final_data, { appointment });
      showSuccessAndClose("Prescription submitted successfully!");
      // Refresh the page after successful submission
      // window.location.reload();
      return data;
    } catch (error) {
      console.error('[NewPrescription] Error creating prescription:', error);
      console.error('[NewPrescription] Error response:', error.response?.data);
      
      // Provide more helpful error message
      let errorMessage = getApiErrorMessage(
        error,
        "Failed to submit prescription. Please try again."
      );
      let shouldRetryWithoutLabTests = false;
      
      if (error.response?.status === 500) {
        const backendMessage = error.response?.data?.message || '';
        
        if (backendMessage.includes('Patient not found')) {
          errorMessage = `Patient not found. The patient with ID ${patientId} does not exist in the organization-specific patient table. Please ensure the patient exists in the correct organization table.`;
        } else if (backendMessage.includes('foreign key constraint') && 
                   backendMessage.includes('path_lab_test_report') &&
                   backendMessage.includes('patient')) {
          // Lab test report foreign key constraint issue
          errorMessage = `Failed to create lab test reports. The backend database schema has a foreign key constraint issue with organization-specific patients. `;
          errorMessage += `The prescription was created but lab tests could not be linked. `;
          errorMessage += `Please contact the administrator to fix the database schema, or try submitting without lab tests.`;
          shouldRetryWithoutLabTests = true;
        } else if (backendMessage) {
          errorMessage = `Backend error: ${backendMessage}`;
        }
      } else if (error.response?.status === 404) {
        errorMessage = 'Prescription endpoint not found. Please check backend configuration.';
      }
      
      // If it's a lab test foreign key issue and user has lab tests, offer to retry without them
      if (shouldRetryWithoutLabTests && selectedLabTests.length > 0) {
        const retryWithoutLabTests = await confirmAsync(
          errorMessage +
            "\n\nWould you like to retry submitting the prescription without lab tests?",
          {
            title: "Retry without lab tests?",
            confirmLabel: "Retry",
            cancelLabel: "Cancel",
            tone: "warning",
          }
        );
        
        if (retryWithoutLabTests) {
          // Retry without lab tests
          const final_data_without_lab_tests = {
            ...final_data,
            patientLabTests: [], // Remove lab tests
          };
          
          try {
            console.log('[NewPrescription] Retrying prescription creation without lab tests...');
            const data = await createPrescription(final_data_without_lab_tests, {
              appointment,
            });
            showSuccessAndClose(
              "Prescription submitted successfully (without lab tests). Lab tests need to be handled separately due to database schema constraints."
            );
            return data; // Exit early if retry was attempted
          } catch (retryError) {
            console.error('[NewPrescription] Error retrying without lab tests:', retryError);
            alert('Failed to submit prescription even without lab tests. Please try again or contact support.');
          }
          return; // Exit early if retry failed too
        }
      }
      
      alert(errorMessage);
    }
    } catch (unexpectedError) {
      console.error("[NewPrescription] Unexpected prescription submit error:", unexpectedError);
      toast.error(
        unexpectedError?.message ||
          "Failed to submit prescription. Please try again.",
        { position: "bottom-center", autoClose: 4000 }
      );
    } finally {
      if (!skipGuard) {
        isSubmittingRef.current = false;
        setSubmitting(false);
      }
    }
  };

  const buildPrescriptionPrintHtml = async (values, savedPrescription = null) => {
    const additionalComplaintsArray = values.additionalComplaints
      ? values.additionalComplaints
          .split(/\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];
    const additionalDiagnosesArray = values.additionalDiagnoses
      ? values.additionalDiagnoses
          .split(/\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];
    const additionalLabTestsArray = values.additionalLabTests
      ? values.additionalLabTests
          .split(/\n|,/)
          .map((item) => item.trim())
          .filter((item) => item.length > 0)
      : [];

    const patientFromDetails = patientDetails || {};

    // Same doctor-row lookup OpdReports.jsx uses for its "Print Summary" flow, so
    // both prescription print paths render identical registration/department/
    // specialization details in the signature block.
    const doctorIdForPrint = resolvePositiveId(
      values?.doctorId,
      appointment?.doctorId,
      appointment?.doctor_id,
      appointment?.doctor?.id
    );
    let doctorDetails = null;
    if (doctorIdForPrint) {
      try {
        const doctors = await getAllDoctors();
        doctorDetails =
          (doctors || []).find((d) => Number(d.id) === Number(doctorIdForPrint)) || null;
      } catch (error) {
        console.warn("Failed to load doctor details for prescription print", error);
      }
    }

    const appointmentsForPrint = (() => {
        const appointmentIdResolved = resolveAppointmentIdFrom(
          values?.appointmentId,
          appointment?.appointmentId,
          appointment?.appointment_id,
          appointment?.id,
          lastKnownAppointmentIdRef.current,
          appointment
        );
        if (appointmentIdResolved != null) {
          lastKnownAppointmentIdRef.current = appointmentIdResolved;
        }
        const hasAppointmentContext =
          appointment &&
          (appointment.appointmentDateTime ||
            appointment.appointmentDate ||
            appointment.date ||
            appointment.scheduledAt ||
            appointment.doctorName ||
            appointmentIdResolved);
        if (!hasAppointmentContext) return [];
        return [
          {
            ...(appointment && typeof appointment === "object" ? appointment : {}),
            id:
              appointmentIdResolved ??
              appointment?.id ??
              appointment?.appointmentId ??
              appointment?.appointment_id ??
              null,
            appointmentId:
              appointmentIdResolved ??
              appointment?.appointmentId ??
              appointment?.id ??
              appointment?.appointment_id ??
              null,
            appointment_id:
              appointmentIdResolved ??
              appointment?.appointment_id ??
              appointment?.appointmentId ??
              appointment?.id ??
              null,
            appointmentDateTime:
              appointment.appointmentDateTime ||
              appointment.appointmentDate ||
              appointment.date ||
              appointment.scheduledAt ||
              null,
            doctorName: appointment.doctorName || "",
          },
        ];
    })();

    return buildOpdSummaryDocumentHtml({
      hospitalName: hospitalName || getOrganizationName(null) || "Hospital",
      hospitalAddress: hospitalAddress || "",
      hospitalPhone: hospitalPhone || "",
      patient: {
        id:
          displayPatientId ||
          patientFromDetails.id ||
          appointment?.patientId ||
          appointment?.patient?.id ||
          appointment?.id ||
          "N/A",
        name:
          displayPatientName ||
          patientFromDetails.name ||
          appointment?.patientName ||
          appointment?.patient?.name ||
          appointment?.name ||
          "Patient",
        gender:
          patientFromDetails.gender ||
          appointment?.patient?.gender ||
          appointment?.gender ||
          "",
        age:
          patientFromDetails.age ||
          appointment?.patient?.age ||
          appointment?.age ||
          "",
        dob:
          patientFromDetails.dob ||
          appointment?.patient?.dob ||
          appointment?.dob ||
          "",
        mobile:
          patientFromDetails.mobile ||
          patientFromDetails.phone ||
          appointment?.patient?.mobile ||
          appointment?.mobile ||
          "",
        address:
          patientFromDetails.address ||
          appointment?.patient?.address ||
          appointment?.address ||
          "",
      },
      visitDate: dayjs().format("DD MMM YYYY, hh:mm A"),
      dayData: {
        prescriptions: [
          {
            id: savedPrescription?.id ?? savedPrescription?.prescriptionId ?? null,
            medicines: (values.medicines || []).filter((med) => med?.name),
            doctorName: appointment?.doctorName || "Doctor",
            advice: values.medicineNotes || "",
            followUpDate: values.nextVisitDate || "",
          },
        ],
        appointments: appointmentsForPrint,
        complaints: [...new Set([...(values.complaints || []), ...additionalComplaintsArray])],
        diagnoses: [...new Set([...(values.diagnoses || []), ...additionalDiagnosesArray])],
        vitalSigns: values.vitalSigns ? [values.vitalSigns] : [],
        LabtestList: [
          ...new Set([...(values.patientLabTests || []), ...additionalLabTestsArray]),
        ].map((testName) => ({ testName })),
      },
      doctorDetails,
    });
  };

  const handlePrint = async (values, savedPrescription = null) => {
    await printPatientSummaryDocument({
      title: "OPD Summary",
      bodyHtml: await buildPrescriptionPrintHtml(values, savedPrescription),
    });
  };

const handleSaveAndPrint = async(values, { validateForm, setSubmitting, setFieldTouched, setFieldValue }) => {
  if (isSubmittingRef.current) return;
  isSubmittingRef.current = true;
  setSubmitting(true);

  try {
    const sanitizedVitals = sanitizeVitalSignsForSubmit(values.vitalSigns || {});
    const appointmentIdResolved = resolveAppointmentIdFrom(
      values?.appointmentId,
      appointment?.appointmentId,
      appointment?.appointment_id,
      appointment?.id,
      lastKnownAppointmentIdRef.current,
      appointment
    );
    if (appointmentIdResolved != null) {
      lastKnownAppointmentIdRef.current = appointmentIdResolved;
      if (typeof setFieldValue === "function") {
        setFieldValue("appointmentId", appointmentIdResolved);
      }
    }

    const valuesForSubmit = {
      ...values,
      vitalSigns: sanitizedVitals,
      appointmentId: appointmentIdResolved ?? values?.appointmentId ?? null,
    };
    if (typeof setFieldValue === "function") {
      Object.entries(sanitizedVitals).forEach(([key, val]) => {
        if (String(values?.vitalSigns?.[key] ?? "") !== String(val ?? "")) {
          setFieldValue(`vitalSigns.${key}`, val);
        }
      });
    }

    // Validate the form first
    const validationErrors = await validateForm(valuesForSubmit);
    
    // Check if there are any validation errors — show field messages + toast
    if (validationErrors && Object.keys(validationErrors).length > 0) {
      touchValidationErrors(validationErrors, setFieldTouched);
      const messages = collectValidationMessages(validationErrors);
      toast.error(
        messages[0] ||
          "Please fill required prescription fields before submitting.",
        { position: "bottom-center", autoClose: 4000 }
      );
      return; // Don't proceed with print or submit
    }
    
    // Save first, then print — printing before save could show a prescription
    // that ends up never persisted (e.g. if submit fails), and the printed
    // Prescription ID needs the id the backend assigns on save.
    const savedPrescription = await handleSubmit(
      valuesForSubmit,
      { setSubmitting, validateForm, setFieldTouched, setFieldValue },
      { skipGuard: true }
    );
    if (savedPrescription) {
      await handlePrint(valuesForSubmit, savedPrescription);
    }
  } finally {
    isSubmittingRef.current = false;
    setSubmitting(false);
  }
}

  return (
    <>
    <div className="App" >
    <Formik
      initialValues={initialValues}
      validationSchema={validationSchema}
      onSubmit={handleSubmit}
    >
      {({
        values,
        handleChange,
        handleBlur,
        setFieldValue,
        setFieldTouched,
        touched,
        errors,
        isSubmitting,
        submitCount,
        validateForm,
        setSubmitting,
      }) => {
        // Helper: numeric fields; BP also allows systolic/diastolic (e.g. 120/80)
        const handleNumericChange = (e, fieldName) => {
          const value = e.target.value;
          const isBpField = fieldName === "vitalSigns.bpReading";
          const pattern = isBpField
            ? /^\d*\.?\d*(\/\d*\.?\d*)?$/
            : /^\d*\.?\d*$/;
          if (value === "" || pattern.test(value)) {
            setFieldValue(fieldName, value);
          }
        };

        return (
        <>
          <PrefillFrontDeskVitals
            frontDeskVitals={frontDeskVitals}
            values={values}
            setFieldValue={setFieldValue}
          />
          <Form>
          <Box sx={{ ...DENSE_FORM_BODY_SX, ...FORM_MODAL_CONTENT_SX }}>
          {/* Patient Info Heading - Only show if showPatientHeading is true (hidden when opened from PatientInQueue/Calendar) */}
          {showPatientHeading && (
            <Grid container spacing={1} sx={{ mb: 0.5 }}>
              <Grid item xs={12}>
                <Typography variant="h5" sx={{ fontWeight: "bold", mb: 0.25 }}>
                  Patient Id {displayPatientId || appointment?.patientId || appointment?.patient?.id || appointment?.id || "N/A"} {displayPatientName || appointment?.patientName || appointment?.patient?.name || appointment?.name || "Patient"}
                </Typography>
                <Divider sx={{ mt: 0.25, mb: 0.5 }} />
              </Grid>
            </Grid>
          )}
          <Grid container spacing={1}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Doctor Name"
                name="doctorId"
                value={appointment?.doctorName || ""}
                disabled={true}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item {...HALF_FIELD_GRID_SX}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.25 }}
              >
                <Typography sx={FORM_SECTION_TITLE_SX}>Complaints</Typography>
                <Tooltip title="Load Previous">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const complaints = Array.isArray(patient_appointment?.complaints)
                          ? patient_appointment.complaints
                          : [];
                        console.log(`[NewPrescription] Loading complaints:`, complaints);
                        setFieldValue("complaints", complaints);
                      }}
                      disabled={
                        fetching || Object.keys(patient_appointment).length === 0
                      }
                    >
                      <UndoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Autocomplete
                multiple
                fullWidth
                options={["Add Custom Complaint...", ...complaints.map((ele) => ele.name)]}
                value={values.complaints}
                onChange={(event, newValue) => {
                  if (newValue && newValue.includes("Add Custom Complaint...")) {
                    const filteredValue = newValue.filter(item => item !== "Add Custom Complaint...");
                    setFieldValue("complaints", filteredValue);
                    setShowAdditionalComplaints(true);
                  } else {
                    setFieldValue("complaints", newValue);
                  }
                }}
                getOptionDisabled={(option) => option === "Add Custom Complaint..." && values.complaints.includes("Add Custom Complaint...")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Complaints"
                    placeholder="Select Complaints"
                    error={(touched.complaints || submitCount > 0) && Boolean(errors.complaints)}
                    helperText={(touched.complaints || submitCount > 0) && errors.complaints}
                    {...DENSE_FIELD_PROPS}
                  />
                )}
              />
            </Grid>

            <Grid item {...HALF_FIELD_GRID_SX}>
              <Box
                display="flex"
                alignItems="center"
                justifyContent="space-between"
                sx={{ mb: 0.25 }}
              >
                <Typography sx={FORM_SECTION_TITLE_SX}>Diagnoses</Typography>
                <Tooltip title="Load Previous">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const diagnoses = Array.isArray(patient_appointment?.diagnoses)
                          ? patient_appointment.diagnoses
                          : [];
                        console.log(`[NewPrescription] Loading diagnoses:`, diagnoses);
                        setFieldValue("diagnoses", diagnoses);
                      }}
                      disabled={
                        fetching || Object.keys(patient_appointment).length === 0
                      }
                    >
                      <UndoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
              <Autocomplete
                fullWidth
                multiple
                options={["Add Custom Diagnosis...", ...diagnosis.map((ele) => ele.diagnosisName)]}
                value={values.diagnoses}
                onChange={(event, newValue) => {
                  if (newValue && newValue.includes("Add Custom Diagnosis...")) {
                    const filteredValue = newValue.filter(item => item !== "Add Custom Diagnosis...");
                    setFieldValue("diagnoses", filteredValue);
                    setShowAdditionalDiagnoses(true);
                  } else {
                    setFieldValue("diagnoses", newValue);
                  }
                }}
                getOptionDisabled={(option) => option === "Add Custom Diagnosis..." && values.diagnoses.includes("Add Custom Diagnosis...")}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Diagnoses"
                    placeholder="Select Diagnoses"
                    error={(touched.diagnoses || submitCount > 0) && Boolean(errors.diagnoses)}
                    helperText={(touched.diagnoses || submitCount > 0) && errors.diagnoses}
                    {...DENSE_FIELD_PROPS}
                  />
                )}
              />
            </Grid>

            {showAdditionalComplaints && (
              <Grid item {...HALF_FIELD_GRID_SX}>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={1}
                    name="additionalComplaints"
                    value={values.additionalComplaints}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter additional complaints manually..."
                    {...DENSE_FIELD_PROPS}
                  />
                  <IconButton
                    onClick={() => {
                      setShowAdditionalComplaints(false);
                      setFieldValue("additionalComplaints", "");
                    }}
                    size="small"
                    sx={{ mt: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {showAdditionalDiagnoses && (
              <Grid item {...HALF_FIELD_GRID_SX}>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={1}
                    name="additionalDiagnoses"
                    value={values.additionalDiagnoses}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter additional diagnoses manually..."
                    {...DENSE_FIELD_PROPS}
                  />
                  <IconButton
                    onClick={() => {
                      setShowAdditionalDiagnoses(false);
                      setFieldValue("additionalDiagnoses", "");
                    }}
                    size="small"
                    sx={{ mt: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* Vital Signs */}
            <Grid item xs={12}>
              <Box
                display="flex"
                flexDirection="row"
                gap={0.5}
                alignItems="center"
                sx={{ mb: 0.25 }}
              >
                <Typography sx={FORM_SECTION_TITLE_SX}>
                  Vital Signs
                </Typography>
                <Tooltip title="Load Previous / Front Desk Vitals">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const vitalSignsData =
                          getLatestVitalSign(patient_appointment?.vitalSigns) ||
                          normalizeVitalSignsRecord(frontDeskVitals) ||
                          {};
                        
                        setFieldValue(
                          "vitalSigns.bpReading",
                          vitalSignsData?.bpReading || ""
                        );
                        setFieldValue(
                          "vitalSigns.weightReading",
                          vitalSignsData?.weightReading || ""
                        );
                        setFieldValue(
                          "vitalSigns.heightReading",
                          vitalSignsData?.heightReading || ""
                        );
                        setFieldValue(
                          "vitalSigns.pulseRate",
                          vitalSignsData?.pulseRate || ""
                        );
                        setFieldValue(
                          "vitalSigns.respirationRate",
                          vitalSignsData?.respirationRate || ""
                        );
                        setFieldValue(
                          "vitalSigns.oxygenSaturation",
                          vitalSignsData?.oxygenSaturation || ""
                        );
                      }}
                      disabled={
                        fetching &&
                        !frontDeskVitals &&
                        Object.keys(patient_appointment).length === 0
                      }
                    >
                      <UndoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>
            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="BP Reading"
                name="vitalSigns.bpReading"
                value={values.vitalSigns.bpReading}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">mmHg</InputAdornment>
                  ),
                }}
                onChange={(e) => handleNumericChange(e, "vitalSigns.bpReading")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.bpReading &&
                  Boolean(errors.vitalSigns?.bpReading)
                }
                helperText={
                  touched.vitalSigns?.bpReading && errors.vitalSigns?.bpReading
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="Weight Reading"
                name="vitalSigns.weightReading"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">kg</InputAdornment>
                  ),
                }}
                value={values.vitalSigns.weightReading}
                onChange={(e) => handleNumericChange(e, "vitalSigns.weightReading")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.weightReading &&
                  Boolean(errors.vitalSigns?.weightReading)
                }
                helperText={
                  touched.vitalSigns?.weightReading &&
                  errors.vitalSigns?.weightReading
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="Height Reading"
                name="vitalSigns.heightReading"
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">cm</InputAdornment>
                  ),
                }}
                value={values.vitalSigns.heightReading}
                onChange={(e) => handleNumericChange(e, "vitalSigns.heightReading")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.heightReading &&
                  Boolean(errors.vitalSigns?.heightReading)
                }
                helperText={
                  touched.vitalSigns?.heightReading &&
                  errors.vitalSigns?.heightReading
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="Pulse Rate"
                name="vitalSigns.pulseRate"
                value={values.vitalSigns.pulseRate}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">bpm</InputAdornment>
                  ),
                }}
                onChange={(e) => handleNumericChange(e, "vitalSigns.pulseRate")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.pulseRate &&
                  Boolean(errors.vitalSigns?.pulseRate)
                }
                helperText={
                  touched.vitalSigns?.pulseRate && errors.vitalSigns?.pulseRate
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="Respiration Rate"
                name="vitalSigns.respirationRate"
                value={values.vitalSigns.respirationRate}
                onChange={(e) => handleNumericChange(e, "vitalSigns.respirationRate")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.respirationRate &&
                  Boolean(errors.vitalSigns?.respirationRate)
                }
                helperText={
                  touched.vitalSigns?.respirationRate &&
                  errors.vitalSigns?.respirationRate
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item {...VITAL_SIGN_GRID_SX}>
              <TextField
                fullWidth
                label="Oxygen Saturation"
                name="vitalSigns.oxygenSaturation"
                value={values.vitalSigns.oxygenSaturation}
                onChange={(e) => handleNumericChange(e, "vitalSigns.oxygenSaturation")}
                onBlur={handleBlur}
                error={
                  touched.vitalSigns?.oxygenSaturation &&
                  Boolean(errors.vitalSigns?.oxygenSaturation)
                }
                helperText={
                  touched.vitalSigns?.oxygenSaturation &&
                  errors.vitalSigns?.oxygenSaturation
                }
                {...DENSE_FIELD_PROPS}
              />
            </Grid>
            <Grid item xs={12}>
              <Box
                display="flex"
                flexDirection="row"
                gap={0.5}
                alignItems="center"
                sx={{ mb: 0.25 }}
              >
                <Typography sx={FORM_SECTION_TITLE_SX}>
                  Medicine
                </Typography>
                <Tooltip title="Load Previous">
                  <span>
                    <IconButton
                      size="small"
                      onClick={() => {
                        const previousMedicines = Array.isArray(patient_appointment.medicines)
                          ? patient_appointment.medicines
                          : [];
                      
                        // Normalize previously loaded medicines into the exact Formik shape
                        // expected by the current prescription form.
                        const normalizedPreviousMedicines = previousMedicines
                          .map((medicine) => {
                            const nestedMedicine =
                              medicine?.medicine ||
                              medicine?.medicineDetails ||
                              medicine?.product ||
                              {};
                      
                            const medicineId =
                              medicine?.medicineId ??
                              medicine?.medicine_id ??
                              medicine?.id ??
                              nestedMedicine?.medicineId ??
                              nestedMedicine?.medicine_id ??
                              nestedMedicine?.id ??
                              null;
                      
                            const medicineName =
                              medicine?.name ||
                              medicine?.medicineName ||
                              medicine?.medicine_name ||
                              nestedMedicine?.name ||
                              nestedMedicine?.medicineName ||
                              nestedMedicine?.medicine_name ||
                              "";
                      
                            return {
                              medicineId:
                                medicineId !== null && medicineId !== undefined && medicineId !== ""
                                  ? medicineId
                                  : null,
                              name: String(medicineName || ""),
                              dosage: medicine?.dosage ?? "",
                              frequency: medicine?.frequency ?? "",
                              duration:
                                medicine?.duration !== null &&
                                medicine?.duration !== undefined
                                  ? String(medicine.duration)
                                  : "",
                              notes: medicine?.notes ?? "",
                            };
                          })
                          .filter((medicine) => medicine.name.trim() !== "");
                      
                        // Keep at least one editable row.
                        setFieldValue(
                          "medicines",
                          normalizedPreviousMedicines.length > 0
                            ? normalizedPreviousMedicines
                            : [
                                {
                                  medicineId: null,
                                  name: "",
                                  dosage: "",
                                  frequency: "",
                                  duration: "",
                                  notes: "",
                                },
                              ]
                        );
                      
                        // Extract common notes from the first medicine (existing behavior).
                        const commonNotes =
                          normalizedPreviousMedicines.length > 0
                            ? normalizedPreviousMedicines[0].notes || ""
                            : "";
                      
                        setFieldValue("medicineNotes", commonNotes);
                      }}
                      disabled={
                        fetching || Object.keys(patient_appointment).length === 0
                      }
                    >
                      <UndoOutlinedIcon fontSize="small" />
                    </IconButton>
                  </span>
                </Tooltip>
              </Box>
            </Grid>

            <Grid item xs={12}>
              <Table
                size="small"
                sx={{
                  "& .MuiTableCell-root": {
                    py: 0.35,
                    px: 0.75,
                    fontSize: "0.75rem",
                  },
                }}
              >
                <TableHead>
                  <TableRow>
                    <TableCell>#</TableCell>
                    <TableCell>Medicine</TableCell>
                    <TableCell sx={{ width: "110px" }}>Dosage</TableCell>
                    <TableCell sx={{ width: "110px" }}>Frequency</TableCell>
                    <TableCell sx={{ width: "120px" }}>Duration</TableCell>
                    <TableCell sx={{ width: "160px" }}>Comments</TableCell>
                    <TableCell>Action</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  <FieldArray name="medicines">
                    {({ push, remove }) => (
                      <>
                        {values.medicines.map((medicine, index) => (
                          <TableRow key={index}>
                            <TableCell>{index + 1}</TableCell>
                            <TableCell>
                              {showManualMedicineInput[index] ? (
                                <Box display="flex" alignItems="flex-start" gap={1}>
                                  <TextField
                                    fullWidth
                                    variant="outlined"
                                    label="Enter Medicine Name"
                                    name={`medicines.${index}.name`}
                                    value={medicine.name}
                                    onChange={(e) => {
                                      const newName = e.target.value;
                                    
                                      setFieldValue(`medicines.${index}.name`, newName);
                                    
                                      // The old ID must not be submitted for a newly typed medicine.
                                      setFieldValue(`medicines.${index}.medicineId`, null);
                                    }}
                                    onBlur={async (e) => {
                                      handleBlur(e);
                                      // Save medicine to base table when user finishes typing
                                      if (medicine.name && medicine.name.trim() && !medicine.medicineId) {
                                        await saveManualMedicine(medicine.name, setFieldValue, index);
                                      }
                                    }}
                                    error={
                                      (touched.medicines?.[index]?.name || submitCount > 0) &&
                                      Boolean(errors.medicines?.[index]?.name)
                                    }
                                    helperText={
                                      (touched.medicines?.[index]?.name || submitCount > 0) &&
                                      errors.medicines?.[index]?.name
                                    }
                                    placeholder="Enter medicine name manually..."
                                    {...DENSE_FIELD_PROPS}
                                  />
                                  <IconButton
                                    onClick={() => {
                                      setShowManualMedicineInput(prev => ({ ...prev, [index]: false }));
                                      setFieldValue(`medicines.${index}.name`, "");
                                      setFieldValue(`medicines.${index}.medicineId`, null);
                                    }}
                                    size="small"
                                    sx={{ mt: 1 }}
                                  >
                                    <CloseIcon />
                                  </IconButton>
                                </Box>
                              ) : (
                                <Autocomplete
                                  options={[
                                    { name: "Create New Medicine...", isCreateOption: true },
                                    ...getDistinctMedicines(medicine_list)
                                  ]}
                                  getOptionLabel={(option) => option.name || option.medicineName || ""}
                                  value={
                                    medicine_list.find(
                                      (option) =>
                                        String(option.id ?? option.medicineId ?? "") ===
                                          String(medicine.medicineId ?? "") ||
                                        String(option.name ?? option.medicineName ?? "").trim() ===
                                          String(medicine.name ?? "").trim()
                                    ) || (
                                      medicine.name
                                        ? {
                                            id: medicine.medicineId,
                                            medicineId: medicine.medicineId,
                                            name: medicine.name,
                                            medicineName: medicine.name,
                                          }
                                        : null
                                    )
                                  }
                                  isOptionEqualToValue={(option, value) => {
                                    if (!option || !value) return false;
                                  
                                    const optionId = option.id ?? option.medicineId;
                                    const valueId = value.id ?? value.medicineId;
                                  
                                    if (
                                      optionId != null &&
                                      valueId != null &&
                                      String(optionId) === String(valueId)
                                    ) {
                                      return true;
                                    }
                                  
                                    const optionName = String(
                                      option.name ?? option.medicineName ?? ""
                                    ).trim().toLowerCase();
                                  
                                    const valueName = String(
                                      value.name ?? value.medicineName ?? ""
                                    ).trim().toLowerCase();
                                  
                                    return Boolean(optionName && valueName && optionName === valueName);
                                  }}
                                  onChange={(event, newValue) => {
                                    if (newValue && newValue.isCreateOption) {
                                      // Show manual medicine input field instead of opening modal
                                      setShowManualMedicineInput(prev => ({ ...prev, [index]: true }));
                                    } else {
                                      const selectedMedicineName =
                                          newValue?.name || newValue?.medicineName || "";

                                        const selectedMedicineId =
                                          newValue?.medicineId ??
                                          newValue?.id ??
                                          null;

                                        setFieldValue(
                                          `medicines.${index}.name`,
                                          selectedMedicineName
                                        );

                                        setFieldValue(
                                          `medicines.${index}.medicineId`,
                                          selectedMedicineId
                                        );
                                      // Hide manual input if medicine is selected from list
                                      setShowManualMedicineInput(prev => ({ ...prev, [index]: false }));
                                    }
                                  }}
                                  getOptionDisabled={(option) => option.isCreateOption && showManualMedicineInput[index]}
                                  renderOption={(props, option) => (
                                    <li {...props} key={option.isCreateOption ? "create-new" : option.id || option.name}>
                                      <Box sx={{ 
                                        fontWeight: option.isCreateOption ? 'bold' : 'normal',
                                        color: option.isCreateOption ? 'primary.main' : 'inherit'
                                      }}>
                                        {option.isCreateOption ? "+ " : ""}{option.name || option.medicineName}
                                      </Box>
                                    </li>
                                  )}
                                  renderInput={(params) => (
                                    <TextField
                                      {...params}
                                      variant="outlined"
                                      label="Select Medicine"
                                      error={
                                        (touched.medicines?.[index]?.name || submitCount > 0) &&
                                        Boolean(errors.medicines?.[index]?.name)
                                      }
                                      helperText={
                                        (touched.medicines?.[index]?.name || submitCount > 0) &&
                                        errors.medicines?.[index]?.name
                                      }
                                      {...DENSE_FIELD_PROPS}
                                    />
                                  )}
                                  fullWidth
                                />
                              )}
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                name={`medicines.${index}.dosage`}
                                value={medicine.dosage}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  (touched.medicines?.[index]?.dosage || submitCount > 0) &&
                                  Boolean(errors.medicines?.[index]?.dosage)
                                }
                                helperText={
                                  (touched.medicines?.[index]?.dosage || submitCount > 0) &&
                                  errors.medicines?.[index]?.dosage
                                }
                                fullWidth
                                {...DENSE_FIELD_PROPS}
                              >
                                {dosageOptions.map((option, idx) => (
                                  <MenuItem key={idx} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                select
                                name={`medicines.${index}.frequency`}
                                value={medicine.frequency}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                error={
                                  (touched.medicines?.[index]?.frequency || submitCount > 0) &&
                                  Boolean(errors.medicines?.[index]?.frequency)
                                }
                                helperText={
                                  (touched.medicines?.[index]?.frequency || submitCount > 0) &&
                                  errors.medicines?.[index]?.frequency
                                }
                                fullWidth
                                {...DENSE_FIELD_PROPS}
                              >
                                {frequencyOptions.map((option, idx) => (
                                  <MenuItem key={idx} value={option}>
                                    {option}
                                  </MenuItem>
                                ))}
                              </TextField>
                            </TableCell>
                            <TableCell>
                              <TextField
                                name={`medicines.${index}.duration`}
                                value={medicine.duration}
                                onChange={(e) => handleNumericChange(e, `medicines.${index}.duration`)}
                                onBlur={handleBlur}
                                InputProps={{
                                  endAdornment: (
                                    <InputAdornment position="end">
                                      days
                                    </InputAdornment>
                                  ),
                                }}
                                error={
                                  (touched.medicines?.[index]?.duration || submitCount > 0) &&
                                  Boolean(errors.medicines?.[index]?.duration)
                                }
                                helperText={
                                  (touched.medicines?.[index]?.duration || submitCount > 0) &&
                                  errors.medicines?.[index]?.duration
                                }
                                fullWidth
                                placeholder="Duration"
                                {...DENSE_FIELD_PROPS}
                              />
                            </TableCell>
                            <TableCell>
                              <TextField
                                name={`medicines.${index}.notes`}
                                value={medicine.notes || ""}
                                onChange={handleChange}
                                onBlur={handleBlur}
                                fullWidth
                                placeholder="Comments"
                                {...DENSE_FIELD_PROPS}
                              />
                            </TableCell>
                            <TableCell>
                              <IconButton
                                onClick={() => remove(index)}
                                aria-label="delete"
                              >
                                <DeleteIcon />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        ))}
                        <TableRow>
                          <TableCell colSpan={7} sx={{ textAlign: "right" }}>
                            <Button
                              variant="contained"
                              color="primary"
                              onClick={() =>
                                push({
                                  name: "",
                                  dosage: "",
                                  frequency: "",
                                  duration: "",
                                  notes: "", // Add notes field for new medicine
                                })
                              }
                              size="small"
                              sx={FORM_SUBMIT_BUTTON_SX}
                            >
                              Add Medicine
                            </Button>
                          </TableCell>
                        </TableRow>
                      </>
                    )}
                  </FieldArray>
                </TableBody>
              </Table>
            </Grid>

            {/* Common Medicine Notes */}
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={2}
                label="Medicine Notes"
                name="medicineNotes"
                value={values.medicineNotes}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.medicineNotes && Boolean(errors.medicineNotes)}
                // helperText={touched.medicineNotes && errors.medicineNotes || "These notes will be applied to all medicines in the prescription"}
                placeholder="Enter common notes for all medicines..."
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            <Grid item {...HALF_FIELD_GRID_SX}>
              <Autocomplete
                multiple
                options={[
                  "Create New Lab Test...",
                  ...labtests.map((ele) => ele.name)
                ]}
                value={values.patientLabTests}
                onChange={(event, newValue) => {
                  // Check if "Create New Lab Test..." was selected
                  if (newValue && newValue.includes("Create New Lab Test...")) {
                    // Remove "Create New Lab Test..." from the array
                    const filteredValue = newValue.filter(item => item !== "Create New Lab Test...");
                    setFieldValue("patientLabTests", filteredValue);
                    // Show manual lab test input field instead of opening modal
                    setShowAdditionalLabTests(true);
                  } else {
                    setFieldValue("patientLabTests", newValue);
                  }
                }}
                getOptionDisabled={(option) => option === "Create New Lab Test..." && showAdditionalLabTests}
                renderOption={(props, option) => (
                  <li {...props} key={option}>
                    <Box sx={{ 
                      fontWeight: option === "Create New Lab Test..." ? 'bold' : 'normal',
                      color: option === "Create New Lab Test..." ? 'primary.main' : 'inherit'
                    }}>
                      {option === "Create New Lab Test..." ? "+ " : ""}{option}
                    </Box>
                  </li>
                )}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    variant="outlined"
                    label="Select Lab Test"
                    placeholder="Select Lab Test"
                    error={
                      touched.patientLabTests && Boolean(errors.patientLabTests)
                    }
                    helperText={
                      touched.patientLabTests && errors.patientLabTests
                    }
                    {...DENSE_FIELD_PROPS}
                  />
                )}
              />
            </Grid>

            <Grid item {...HALF_FIELD_GRID_SX}>
              <TextField
                fullWidth
                type="date"
                label="Next Visit Date"
                name="nextVisitDate"
                value={values.nextVisitDate}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.nextVisitDate && Boolean(errors.nextVisitDate)}
                helperText={touched.nextVisitDate && errors.nextVisitDate}
                InputLabelProps={{ shrink: true }}
                inputProps={{
                  min: new Date().toISOString().split("T")[0],
                }}
                {...DENSE_FIELD_PROPS}
              />
            </Grid>

            {showAdditionalLabTests && (
              <Grid item {...HALF_FIELD_GRID_SX}>
                <Box display="flex" alignItems="flex-start" gap={0.5}>
                  <TextField
                    fullWidth
                    multiline
                    rows={1}
                    name="additionalLabTests"
                    value={values.additionalLabTests || ""}
                    onChange={handleChange}
                    onBlur={handleBlur}
                    placeholder="Enter additional lab tests manually..."
                    {...DENSE_FIELD_PROPS}
                  />
                  <IconButton
                    onClick={() => {
                      setShowAdditionalLabTests(false);
                      setFieldValue("additionalLabTests", "");
                    }}
                    size="small"
                    sx={{ mt: 0.5 }}
                  >
                    <CloseIcon fontSize="small" />
                  </IconButton>
                </Box>
              </Grid>
            )}

            {/* <Grid item xs={6}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={values.isExtraMedicine}
                    onChange={handleChange}
                    name="isExtraMedicine"
                    color="primary"
                  />
                }
                label="Is Extra Medicine Required?"
              />
            </Grid>
            <Grid item xs={6}></Grid>
            <Grid item xs={6}>
              <TextField
                fullWidth
                label="No of days"
                name="numberOfDays"
                value={values.numberOfDays}
                disabled={!values.isExtraMedicine}
                onChange={handleChange}
                onBlur={handleBlur}
                error={touched.numberOfDays && Boolean(errors.numberOfDays)}
                helperText={touched.numberOfDays && errors.numberOfDays}
                InputLabelProps={{ shrink: true }}
              />
            </Grid> */}

            <Grid item xs={12}>
              <Box sx={FORM_MODAL_FOOTER_SX}>
                <Divider sx={{ mb: 0.5 }} />
                <Box sx={FORM_MODAL_FOOTER_ACTIONS_SX}>
                  <Button
                    size="small"
                    variant="outlined"
                    color="primary"
                    onClick={() => onClose?.()}
                    disabled={isSubmitting}
                    sx={FORM_CANCEL_BUTTON_SX}
                  >
                    Cancel
                  </Button>
                  <Button
                    size="small"
                    type="submit"
                    variant="contained"
                    color="primary"
                    disabled={isSubmitting}
                    sx={FORM_SUBMIT_BUTTON_SX}
                  >
                    Submit
                  </Button>
                  <Button
                    size="small"
                    variant="contained"
                    color="primary"
                    onClick={async () => {
                      await handleSaveAndPrint(values, {
                        validateForm,
                        setSubmitting,
                        setFieldTouched,
                        setFieldValue,
                      });
                    }}
                    disabled={isSubmitting}
                    sx={FORM_SUBMIT_BUTTON_SX}
                  >
                    Submit and Print
                  </Button>
                </Box>
              </Box>
            </Grid>
          </Grid>
          </Box>
        </Form>
      
       {/* Create New Medicine Modal */}
       <AddNewMedicine
         open={openCreateMedicineModal}
         onClose={async () => {
           const savedIndex = currentMedicineIndex; // Save index before resetting
           setOpenCreateMedicineModal(false);
           setCurrentMedicineIndex(null);
           
           // Refresh medicine list from base table
           await fetchMedicine();
           
           // Auto-select the newly created medicine if we have an index
           if (savedIndex !== null) {
             // Fetch updated medicine list from base table
             const updatedMedicineList = await getAllMedicine();
             
             // Find the latest medicine (highest ID)
             const latestMedicine = Array.isArray(updatedMedicineList) && updatedMedicineList.length > 0
               ? updatedMedicineList.reduce((latest, current) => {
                   const latestId = latest.id || 0;
                   const currentId = current.id || 0;
                   return currentId > latestId ? current : latest;
                 })
               : null;
             
             if (latestMedicine) {
               const medicineName = latestMedicine.medicineName || latestMedicine.name;
               const medicineId = latestMedicine.id;
               if (medicineName && medicineId) {
                 setFieldValue(`medicines.${savedIndex}.name`, medicineName);
                 setFieldValue(`medicines.${savedIndex}.medicineId`, medicineId);
               }
             }
           }
         }}
         refreshData={async () => {
           await fetchMedicine();
         }}
       />

       {/* Create New Lab Test Modal */}
       <CreateNewLabTest
         open={openCreateLabTestModal}
         onClose={async (didChange = false) => {
           setOpenCreateLabTestModal(false);
           if (!didChange) return;

           // Refresh lab test list from base table
           await fetchLabtests();
           
           // Auto-select the newly created lab test
           const updatedLabTests = await getAllLabTests();
           const latestLabTest = Array.isArray(updatedLabTests) && updatedLabTests.length > 0
             ? updatedLabTests.reduce((latest, current) => {
                 const latestId = latest.id || 0;
                 const currentId = current.id || 0;
                 return currentId > latestId ? current : latest;
               })
             : null;
           
           if (latestLabTest && latestLabTest.name) {
             const currentLabTests = values.patientLabTests || [];
             if (!currentLabTests.includes(latestLabTest.name)) {
               setFieldValue("patientLabTests", [...currentLabTests, latestLabTest.name]);
             }
           }
         }}
         product_info={null}
         createInBaseTable={true}
       />
       </>
        );
      }}
    </Formik>
   
    </div>

    <Dialog
      open={successDialog.open}
      onClose={handleSuccessDialogClose}
      maxWidth="xs"
      fullWidth
      sx={{
        "& .MuiDialog-container": { alignItems: "flex-start" },
        "& .MuiDialog-paper": {
          margin: STANDARD_FORM_DIALOG_MARGIN,
          maxHeight: STANDARD_FORM_MODAL_MAX_HEIGHT,
          width: "min(520px, 96vw)",
          maxWidth: "520px",
          borderRadius: "8px",
          boxSizing: "border-box",
        },
      }}
    >
      <DialogTitle
        sx={{
          pb: 1,
          fontWeight: 700,
          fontSize: "1.25rem",
          textAlign: "center",
        }}
      >
        Success
      </DialogTitle>
      <DialogContent sx={{ pt: "4px !important", pb: 1 }}>
        <Box display="flex" justifyContent="center" mb={0.75}>
          <Alert severity="success" sx={{ width: "100%", py: 0.75 }}>
            {successDialog.message}
          </Alert>
        </Box>
      </DialogContent>
      <DialogActions sx={{ justifyContent: "center", px: 2, pb: 1.5 }}>
        <Button
          size="small"
          variant="contained"
          color="primary"
          sx={FORM_SUBMIT_BUTTON_SX}
          onClick={handleSuccessDialogClose}
        >
          OK
        </Button>
      </DialogActions>
    </Dialog>
    </>
  );
};

export default NewPrescription;